import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Clock, MapPin, AlertCircle, Check, X, RefreshCw, Loader2, WifiOff, Camera as CameraIcon } from 'lucide-react';
import { styles, injectAnimationStyles } from './ClockInOut.styles';
import { offlineService } from '../../services/offline.service';
import { backgroundGeolocationService } from '../../services/background-geolocation.service';
import { BackgroundLocationDisclosure } from '../common/BackgroundLocationDisclosure';
import { useLocation } from '../../contexts/LocationContext';
import { Capacitor } from '@capacitor/core';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { Camera } from '../Camera';
import { openNativeCamera, fileToDataUrl } from '../../utils/camera.utils';
import TimecardReviewModal from './TimecardReviewModal';
import { easternNow } from '../../utils/easternTime';
import { VacationBanner } from '../common/VacationBanner';
import { CustomTimePicker } from '../common/CustomTimePicker';

// Type definitions
interface Job {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  address?: string;
  city: string;
  state?: string;
  lat: number;
  lng: number;
  geofence_type?: 'circle' | 'polygon';
  geofence_data?: {
    radius?: number;
    points?: Array<{ lat: number; lng: number }>;
  };
  geofence_enabled?: boolean;
}

interface JobWithDistance extends Job {
  distance?: string;
  distanceValue?: number;
  distanceInMiles?: number;
  isWithinGeofence?: boolean;
}

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
  crew_foreman_id?: number;
  role?: string;
}

interface TimeEntry {
  id: number;
  entry_id: number;
  employee_id: number;
  job_id: number;
  clock_in: string;
  clock_out?: string;
  clock_in_lat?: number;
  clock_in_lng?: number;
  lunch_minutes?: number;
  lunch_taken?: boolean;
  hours_worked?: number;
  clocked_at_shop?: boolean;
  shop_work_all_day?: boolean;
  labor_code?: string;
  job_name?: string;
  bradanna_job_number?: string;
}

interface Location {
  lat: number;
  lng: number;
}

interface ShopLocation {
  id: number;
  name: string;
  lat: number;
  lng: number;
  radius: number;
}

interface WeekDay {
  date: string;
  displayDate: string;
  dayName: string;
  dayShort: string;
  hours: number;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const LUNCH_CHOICE_CREW_IDS = [2]; // Crews that can choose lunch
const SHOP_JOB_IDS = [1, 2]; // Shop job IDs (Main Shop and North Shop)
const MINIMUM_SHIFT_FOR_LUNCH = 360; // 6 hours in minutes (changed from 4 to 6)
const LATE_SHIFT_REMINDER_HOUR = 19; // 7 PM (19:00) - "I forgot to clock out" button only shows after this hour
const NEARBY_RADIUS_MILES = 0.5; // Show jobs within half mile
// Grace buffers beyond the fence edge. Clock-out gets a generous one — crews
// park, fuel, or stage just past the fence at day's end. Clock-in gets a small
// one that ONLY applies when the selected job is the sole job within half a
// mile, so there's no risk of starting hours on the wrong adjacent job.
const CLOCK_OUT_GEOFENCE_BUFFER_M = 200;
const CLOCK_IN_GEOFENCE_BUFFER_M = 50;
const LOCATION_STORAGE_KEY = 'bradanna_cached_location'; // Must match LocationContext
const LOCATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

const SHOP_LOCATIONS: ShopLocation[] = [
  {
    id: 1,
    name: 'Main Shop',
    lat: 26.171185,
    lng: -81.677528,
    radius: 300
  },
  {
    id: 2,
    name: 'North Shop',
    lat: 26.6062,
    lng: -81.9807,
    radius: 300
  }
];

const checkAndRefreshAuth = async () => {
  try {
    // Try a simple API call to verify token
    const response = await fetch(`${API_BASE_URL}/api/auth/verify`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
      }
    });
    
    if (!response.ok) {
      console.log('Token invalid, need to re-login');
      window.location.href = '/login';
    }
  } catch (error) {
    console.error('Auth check failed:', error);
  }
};

export const ClockInOut: React.FC = () => {
  const navigate = useNavigate();
  const { location, isLoading: locationLoading, error: locationError, requestLocation, forceRefreshLocation } = useLocation();

  // State management
  const [user, setUser] = useState<Employee | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [currentStatus, setCurrentStatus] = useState<'clocked_in' | 'not_clocked_in' | 'loading'>('loading');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [nearbyJobs, setNearbyJobs] = useState<JobWithDistance[]>([]);
  const [allJobsWithDistance, setAllJobsWithDistance] = useState<JobWithDistance[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobWithDistance | null>(null);
  const [userAtJobSite, setUserAtJobSite] = useState(false);
  // When the user is standing inside 2+ job geofences, don't silently
  // auto-pick the nearest — prompt them to choose to avoid wrong clock-ins.
  const [multiSiteOptions, setMultiSiteOptions] = useState<JobWithDistance[]>([]);
  const [showMultiSitePrompt, setShowMultiSitePrompt] = useState(false);
  // Once dismissed, don't keep re-opening on every GPS tick; resets when the
  // user leaves the overlapping area (back to <2 geofences).
  const multiSiteDismissedRef = useRef(false);
  const [locationAccuracy, setLocationAccuracy] = useState<number | null>(null);
  const [loadingStates, setLoadingStates] = useState({
    user: true,
    jobs: true,
    clockStatus: true
  });
  const [weeklyHours, setWeeklyHours] = useState<WeekDay[]>([]);
  const [totalWeekHours, setTotalWeekHours] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [showLaborCodeSelector, setShowLaborCodeSelector] = useState(false);
  const [showLunchConfirm, setShowLunchConfirm] = useState(false);
  const [showShopWorkConfirm, setShowShopWorkConfirm] = useState(false);
  const [shopWorkConfirmed, setShopWorkConfirmed] = useState(false);
  const [lunchTaken, setLunchTaken] = useState(true);
  const [currentShopLocation, setCurrentShopLocation] = useState<ShopLocation | null>(null);
  const [clockedInAtShop, setClockedInAtShop] = useState(false);
  const [shopClockInTime, setShopClockInTime] = useState<Date | null>(null);
  const [isTemporaryShopClock, setIsTemporaryShopClock] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showOpenEntryModal, setShowOpenEntryModal] = useState(false);
  const [openEntry, setOpenEntry] = useState<TimeEntry | null>(null);
  const [showConvertPrompt, setShowConvertPrompt] = useState(false);
  const [targetJob, setTargetJob] = useState<JobWithDistance | null>(null);
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);

  // Clock-in confirmation state
  const [showClockInConfirmation, setShowClockInConfirmation] = useState(false);
  const [clockInConfirmDetails, setClockInConfirmDetails] = useState<{jobName: string, time: string} | null>(null);
  const [checkingSpecialRequirements, setCheckingSpecialRequirements] = useState(false);

  // New states for forgot clock out functionality
  const [showForgotClockOut, setShowForgotClockOut] = useState(false);
  const [forgotClockOutMethod, setForgotClockOutMethod] = useState<'location_data' | 'crew_average' | 'manual'>('crew_average');
  const [manualClockOutTime, setManualClockOutTime] = useState('16:30');
  const [manualTimeAmPm, setManualTimeAmPm] = useState<'AM' | 'PM'>('PM');
  const [crewAverageTime, setCrewAverageTime] = useState<string | null>(null);
  const [showForgotButton, setShowForgotButton] = useState(false);
  const [suggestedExitTime, setSuggestedExitTime] = useState<{time: string; jobName: string; eventTime: string} | null>(null);

  // Offline/network status
  const [isOffline, setIsOffline] = useState(false);
  const [usingCachedStatus, setUsingCachedStatus] = useState(false);

  // Unknown job clock-in setting
  const [allowUnknownJobClockin, setAllowUnknownJobClockin] = useState(false);

  // Vacation schedule state
  const [vacationSchedule, setVacationSchedule] = useState<{ start_date: string; end_date: string; hours_requested: number } | null>(null);

  // Unknown job photo capture
  const [showUnknownJobCamera, setShowUnknownJobCamera] = useState(false);
  const [unknownJobPhoto, setUnknownJobPhoto] = useState<string | null>(null);
  const [unknownJobPhotoFile, setUnknownJobPhotoFile] = useState<File | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pendingUnknownJobClockIn, setPendingUnknownJobClockIn] = useState<{
    jobToClockIn: JobWithDistance;
    laborCode: string;
    shopWorkAllDay: boolean;
    isTemporary: boolean;
    lat: number;
    lng: number;
  } | null>(null);

  // Day details modal
  const [showDayDetails, setShowDayDetails] = useState(false);
  const [selectedDay, setSelectedDay] = useState<WeekDay | null>(null);

  // Background location disclosure (Google Play requirement)
  const [showBackgroundLocationDisclosure, setShowBackgroundLocationDisclosure] = useState(false);
  const [pendingBackgroundTrackingJobId, setPendingBackgroundTrackingJobId] = useState<number | null>(null);
  const BACKGROUND_LOCATION_ACCEPTED_KEY = 'bradanna_background_location_accepted';
  const [dayEntries, setDayEntries] = useState<TimeEntry[]>([]);

  // Location permission required modal (for clock-in)
  const [showPermissionRequired, setShowPermissionRequired] = useState(false);

  // Timecard review modal (Friday approval)
  const [showTimecardReview, setShowTimecardReview] = useState(false);
  // Instant feedback while the clock-in button runs its pre-checks (location +
  // Friday review data pull can take a few seconds — show a spinner immediately
  // so the tap doesn't feel ignored).
  const [checkingClockIn, setCheckingClockIn] = useState(false);
  // GPS failed during a clock action — show the friendly retry modal instead of
  // the generic error modal, remembering which action to re-fire on success.
  const [locationRetry, setLocationRetry] = useState<null | { action: 'in'; laborCode: string } | { action: 'out' }>(null);
  const [locationRetryStatus, setLocationRetryStatus] = useState<'idle' | 'trying' | 'failed'>('idle');
  const [timecardReviewData, setTimecardReviewData] = useState<{
    weekStartDate: string;
    weekEndDate: string;
    entries: any[];
    totalHours: string;
    crewAverageClockOut: string;
    canSkip: boolean;
  } | null>(null);
  const [timecardApprovalEnabled, setTimecardApprovalEnabled] = useState(false);
  const [timecardApprovalMandatory, setTimecardApprovalMandatory] = useState(false);
  const [timecardApprovalTestMode, setTimecardApprovalTestMode] = useState(false);

  const successTimeout = useRef<NodeJS.Timeout | null>(null);
  const lateShiftCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const lastActionTime = useRef<number>(0);
  const userRef = useRef<Employee | null>(null); // Ref to access current user in event handlers
  const isCapturingPhotoRef = useRef<boolean>(false); // Ref to track if camera is active (for visibility handler)

  // Translation system
  const getTranslations = (lang: 'en' | 'es') => ({
    en: {
      timeClockTitle: 'Time',
      clockedIn: 'CLOCKED IN',
      notClockedIn: 'NOT CLOCKED IN',
      currentJob: 'CURRENT JOB',
      notAtJobSite: 'You are not at any job site',
      selectJobFirst: 'Please select a job to continue',
      nearbyJobs: 'Nearby Job Sites (within 0.5 miles)',
      noNearbyJobs: 'No job sites within 0.5 miles',
      atLocation: 'At Location',
      milesAway: 'miles away',
      selected: '(Selected)',
      gettingLocation: 'Getting location...',
      selectJob: 'Select a job',
      clockIn: 'Clock In',
      clockOut: 'Clock Out',
      changeJob: 'CHANGE JOB',
      convertToJob: 'SWITCH JOB',
      startDay: 'Start work',
      endDay: 'End work',
      weeklyHours: "This Week",
      weekPeriod: 'Fri - Thu',
      totalHours: 'TOTAL',
      hours: 'hrs',
      selectLaborCode: 'Select Work Type',
      cancel: 'Cancel',
      lunchQuestion: 'Did you take lunch?',
      lunchNote: '30 minutes will be deducted',
      yesLunch: 'Yes, took lunch',
      noLunch: 'No lunch',
      mustBeAtSite: 'Must be at job site',
      locationRequired: 'Location is required',
      lowAccuracy: 'Low GPS accuracy',
      shopWork: 'Shop',
      atShop: 'At shop',
      temporaryShopClock: 'Temporary shop clock',
      shopWorkConfirmTitle: 'Working at Shop All Day?',
      shopWorkConfirmMessage: 'You selected SHOP. This means you will work at the shop for your entire shift today. Is this correct?',
      shopWorkConfirmYes: 'Yes, Shop All Day',
      shopWorkConfirmNo: 'No, Just Temporary',
      convertPromptTitle: 'Convert to Job?',
      convertPromptMessage: 'You started at shop. Convert to this job?',
      yes: 'Yes',
      no: 'No',
      forgotClockOut: 'Forgot to Clock Out?',
      closeEntry: 'Yes, Close It',
      keepOpen: 'No, Keep Open',
      cannotChangeJob: 'Cannot change job while at shop',
      mustBeAtTargetJob: 'Must be at target job site to change',
      mustBeAtJobSite: 'You must be at the job site or shop to clock out',
      loading: 'Loading...',
      initializing: 'Initializing...',
      submit: 'Submit',
      back: 'Back',
      ok: 'OK',
      retry: 'Retry',
      locationPermissionDenied: 'Location permission denied. This app requires location access to function. If using Incognito/Private mode, please use a regular browser window instead. Otherwise, enable location in your browser settings.',
      locationUnavailable: 'GPS signal unavailable. Try moving to an open area with clear sky view.',
      locationTimeout: 'Location timeout. Check your GPS signal and try again.',
      locationDisabled: 'Location services disabled. Enable GPS in your device settings.',
      locationRequired2: 'Location is required to use the time clock.',
      // New forgot clock out translations
      iForgotToClockOut: 'I FORGOT TO CLOCK OUT',
      forgotClockOutTitle: 'Open Entry Found',
      useCrewAverage: 'Use Crew Average',
      enterTimeManually: 'Enter Time Manually',
      calculating: 'Calculating...',
      confirm: 'Close Entry',
      manualEntryNote: 'Manual entries are flagged for supervisor review',
      // Location prompt translations
      selectYourLocation: 'Select Your Location',
      selectLocationMessage: 'Tap OK to share your location and select your job site.',
      okContinue: 'OK'
    },
    es: {
      timeClockTitle: 'Tiempo',
      clockedIn: 'REGISTRADO',
      notClockedIn: 'NO REGISTRADO',
      currentJob: 'TRABAJO ACTUAL',
      notAtJobSite: 'No estás en ningún sitio de trabajo',
      selectJobFirst: 'Por favor seleccione un trabajo para continuar',
      nearbyJobs: 'Sitios Cercanos (dentro de 0.5 millas)',
      noNearbyJobs: 'No hay sitios dentro de 0.5 millas',
      atLocation: 'En Ubicación',
      milesAway: 'millas de distancia',
      selected: '(Seleccionado)',
      gettingLocation: 'Obteniendo ubicación...',
      selectJob: 'Seleccionar un trabajo',
      clockIn: 'Entrada',
      clockOut: 'Salida',
      changeJob: 'CAMBIAR TRABAJO',
      convertToJob: 'CAMBIAR TRABAJO',
      startDay: 'Iniciar trabajo',
      endDay: 'Terminar trabajo',
      weeklyHours: 'Esta Semana',
      weekPeriod: 'Vie - Jue',
      totalHours: 'TOTAL',
      hours: 'hrs',
      selectLaborCode: 'Seleccionar Tipo de Trabajo',
      cancel: 'Cancelar',
      lunchQuestion: '¿Tomó almuerzo?',
      lunchNote: 'Se deducirán 30 minutos',
      yesLunch: 'Sí, tomé almuerzo',
      noLunch: 'Sin almuerzo',
      mustBeAtSite: 'Debe estar en sitio de trabajo',
      locationRequired: 'Se requiere ubicación',
      lowAccuracy: 'GPS con baja precisión',
      shopWork: 'Taller',
      atShop: 'En taller',
      temporaryShopClock: 'Registro temporal de taller',
      shopWorkConfirmTitle: '¿Trabajando en Taller Todo el Día?',
      shopWorkConfirmMessage: 'Seleccionó TALLER. Esto significa que trabajará en el taller todo su turno hoy. ¿Es correcto?',
      shopWorkConfirmYes: 'Sí, Taller Todo el Día',
      shopWorkConfirmNo: 'No, Solo Temporal',
      convertPromptTitle: '¿Convertir a Trabajo?',
      convertPromptMessage: 'Comenzó en el taller. ¿Convertir a este trabajo?',
      yes: 'Sí',
      no: 'No',
      forgotClockOut: '¿Olvidó marcar salida?',
      closeEntry: 'Sí, Cerrar',
      keepOpen: 'No, Mantener Abierto',
      cannotChangeJob: 'No puede cambiar trabajo mientras está en el taller',
      mustBeAtTargetJob: 'Debe estar en el sitio del trabajo objetivo para cambiar',
      mustBeAtJobSite: 'Debe estar en el sitio de trabajo o taller para marcar salida',
      loading: 'Cargando...',
      initializing: 'Inicializando...',
      submit: 'Enviar',
      back: 'Atrás',
      ok: 'OK',
      retry: 'Reintentar',
      locationPermissionDenied: 'Permiso de ubicación denegado. Esta aplicación requiere acceso a la ubicación para funcionar. Si usa el modo Incógnito/Privado, use una ventana normal del navegador. De lo contrario, habilite la ubicación en la configuración de su navegador.',
      locationUnavailable: 'Señal GPS no disponible. Intente moverse a un área abierta con vista despejada del cielo.',
      locationTimeout: 'Tiempo de espera de ubicación agotado. Verifique su señal GPS e intente nuevamente.',
      locationDisabled: 'Servicios de ubicación deshabilitados. Active el GPS en la configuración de su dispositivo.',
      locationRequired2: 'Se requiere ubicación para usar el reloj de tiempo.',
      // New forgot clock out translations
      iForgotToClockOut: 'OLVIDÉ MARCAR SALIDA',
      forgotClockOutTitle: 'Entrada Abierta Encontrada',
      useCrewAverage: 'Usar Promedio del Equipo',
      enterTimeManually: 'Ingresar Hora Manualmente',
      calculating: 'Calculando...',
      confirm: 'Cerrar Entrada',
      manualEntryNote: 'Las entradas manuales se marcan para revisión del supervisor',
      // Location prompt translations
      selectYourLocation: 'Selecciona Tu Ubicación',
      selectLocationMessage: 'Toca OK para compartir tu ubicación y seleccionar tu sitio de trabajo.',
      okContinue: 'OK'
    }
  }[lang]);

  // Get current translations
  const t = getTranslations(language);

  // Labor codes with dynamic language support
  // Filter out SHOP code if not at shop location to prevent accidental misuse
  const getLaborCodes = () => {
    const allCodes = [
      { code: 'ASPHALT', name: language === 'es' ? 'Asfalto' : 'Asphalt' },
      { code: 'HAUL', name: language === 'es' ? 'Camión/Acarreo' : 'Dump Truck/Hauling' },
      { code: 'EQHAUL', name: language === 'es' ? 'Acarreo de Equipo' : 'Equipment Hauling' },
      { code: 'FGRADE', name: language === 'es' ? 'Nivelación Final' : 'Finish Grading' },
      { code: 'LABOR', name: language === 'es' ? 'Trabajo General' : 'General Labor' },
      { code: 'GRADE', name: language === 'es' ? 'Nivelación' : 'Grading' },
      { code: 'IMPORT', name: language === 'es' ? 'Material de Relleno' : 'Import Fill' },
      { code: 'DEMO', name: language === 'es' ? 'Demolición' : 'Demo' },
      { code: 'LIMEROCK', name: language === 'es' ? 'Piedra Caliza' : 'Limerock' },
      { code: 'SHOP', name: language === 'es' ? 'Taller' : 'Shop' },
      { code: 'SUBGRADE', name: language === 'es' ? 'Subrasante' : 'Subgrade' },
      { code: 'TM', name: language === 'es' ? 'Tiempo y Material' : 'T&M Time & Material' }
    ];

    // Only show SHOP labor code when physically at a shop location
    if (!currentShopLocation) {
      return allCodes.filter(code => code.code !== 'SHOP');
    }

    return allCodes;
  };

  // Utility functions
  const showSuccess = (message: string) => {
    setSuccessMessage(message);

    // Vibrate on success (if supported)
    if ('vibrate' in navigator) {
      navigator.vibrate([100, 50, 100]); // Double vibration pattern
    }

    if (successTimeout.current) {
      clearTimeout(successTimeout.current);
    }
    successTimeout.current = setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  const isValidGPSAccuracy = (): boolean => {
    if (!locationAccuracy) return false;
    return locationAccuracy <= 200; // Max 200m accuracy for geofence operations
  };

  const preventDoubleTap = (): boolean => {
    const now = Date.now();
    if (now - lastActionTime.current < 2000) {
      return true; // Prevent action if less than 2 seconds
    }
    lastActionTime.current = now;
    return false;
  };

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;
    
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  };

  const metersToMiles = (meters: number): number => {
    return meters * 0.000621371;
  };

  const isInsideCircle = (userLat: number, userLng: number, centerLat: number, centerLng: number, radius: number): boolean => {
    const distance = calculateDistance(userLat, userLng, centerLat, centerLng);
    return distance <= radius;
  };

  const isInsidePolygon = (userLat: number, userLng: number, points: Array<{ lat: number; lng: number }>): boolean => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].lat, yi = points[i].lng;
      const xj = points[j].lat, yj = points[j].lng;
      
      const intersect = ((yi > userLng) !== (yj > userLng))
          && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isUserWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return isInsideCircle(userLat, userLng, job.lat, job.lng, job.geofence_data.radius);
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return isInsidePolygon(userLat, userLng, job.geofence_data.points);
    }
    // Default circular geofence of 100 meters
    const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  }, []);

  // Fence test with a grace buffer: inside the fence OR within bufferM of its
  // edge. For polygons the extent is approximated by the farthest vertex from
  // the job center, which is slightly generous at corners — acceptable here,
  // where the question is "in the area", not the exact fence line.
  const isNearJobWithBuffer = useCallback((job: Job, userLat: number, userLng: number, bufferM: number): boolean => {
    if (isUserWithinJobGeofence(job, userLat, userLng)) return true;
    const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
    let fenceExtent = 100;
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      fenceExtent = job.geofence_data.radius;
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      fenceExtent = Math.max(...job.geofence_data.points.map(p =>
        calculateDistance(job.lat, job.lng, p.lat, p.lng)
      ));
    }
    return distance <= fenceExtent + bufferM;
  }, [isUserWithinJobGeofence]);

  // The clock-in buffer only applies when this job is the SOLE job within the
  // nearby radius — with two candidate sites in range, stay strict so hours
  // can't start on the wrong one.
  const isSoleNearbyJob = useCallback((jobId: number): boolean => {
    const nearby = allJobsWithDistance.filter(j =>
      (j.distanceInMiles ?? Infinity) <= NEARBY_RADIUS_MILES
    );
    return nearby.length === 1 && nearby[0].job_id === jobId;
  }, [allJobsWithDistance]);

  const formatDateForComparison = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if user can choose lunch (special crew)
  const canChooseLunch = (): boolean => {
    return user ? LUNCH_CHOICE_CREW_IDS.includes(user.crew_foreman_id || 0) : false;
  };

  // Calculate shift duration in minutes
  const getShiftDurationMinutes = (clockInTime: Date): number => {
    const now = new Date();
    return (now.getTime() - clockInTime.getTime()) / (1000 * 60);
  };

  // Check if shift qualifies for lunch deduction (6+ hours)
  const qualifiesForLunch = (clockInTime: Date): boolean => {
    return getShiftDurationMinutes(clockInTime) >= MINIMUM_SHIFT_FOR_LUNCH;
  };

  // Calculate distances and nearby jobs
  const calculateDistances = useCallback(() => {
    if (!jobs.length) {
      setAllJobsWithDistance([]);
      setNearbyJobs([]);
      setUserAtJobSite(false);
      return;
    }

    // Use location from context, or fall back to localStorage (iOS Safari fix)
    let userLat: number | null = location?.latitude ?? null;
    let userLng: number | null = location?.longitude ?? null;

    if (!userLat || !userLng) {
      // Try localStorage as fallback (iOS Safari can have context sync issues)
      try {
        const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (cached) {
          const parsedLocation = JSON.parse(cached);
          const age = Date.now() - parsedLocation.timestamp;
          if (age < LOCATION_CACHE_DURATION) {
            userLat = parsedLocation.latitude;
            userLng = parsedLocation.longitude;
            console.log('[calculateDistances] Using localStorage location fallback');
          }
        }
      } catch (e) {
        console.error('[calculateDistances] Error reading localStorage:', e);
      }
    }

    // If still no location, show jobs without distance data
    if (!userLat || !userLng) {
      console.log('[calculateDistances] No location available, showing jobs without distance');
      setAllJobsWithDistance(jobs.map(job => ({ ...job })));
      setNearbyJobs([]);
      setUserAtJobSite(false);
      return;
    }

    const jobsWithDist: JobWithDistance[] = jobs.map(job => {
      const distanceInMeters = calculateDistance(
        userLat!,
        userLng!,
        job.lat,
        job.lng
      );

      const distanceInMiles = metersToMiles(distanceInMeters);
      const isWithinGeofence = isUserWithinJobGeofence(job, userLat!, userLng!);

      return {
        ...job,
        distance: `${distanceInMiles.toFixed(2)} ${t.milesAway}`,
        distanceValue: distanceInMeters,
        distanceInMiles: distanceInMiles,
        isWithinGeofence: isWithinGeofence
      };
    }).sort((a, b) => (a.distanceValue || 0) - (b.distanceValue || 0));
    
    setAllJobsWithDistance(jobsWithDist);
    
    // Find job user is at (within geofence)
    const jobUserIsAt = jobsWithDist.find(job => job.isWithinGeofence);
    setUserAtJobSite(!!jobUserIsAt);

    // Detect if user is at a shop location and set currentShopLocation.
    // (Restored verbatim — this block was accidentally deleted by commit
    // 11c2c96 on 2026-05-30, which silently killed clocked_out_at_shop,
    // clocked_at_shop, keep_job_billing, and SHOP labor-code visibility.)
    const shopJobAtLocation = jobsWithDist.find(job =>
      job.isWithinGeofence && SHOP_JOB_IDS.includes(job.job_id)
    );
    if (shopJobAtLocation) {
      setCurrentShopLocation({
        id: shopJobAtLocation.job_id,
        name: shopJobAtLocation.job_name,
        lat: shopJobAtLocation.lat,
        lng: shopJobAtLocation.lng,
        radius: shopJobAtLocation.geofence_data?.radius || 200
      });
      console.log('🏪 User is at shop location:', shopJobAtLocation.job_name);
    } else {
      setCurrentShopLocation(null);
    }

    // Filter nearby jobs (within 0.5 miles OR within geofence)
    const nearby = jobsWithDist.filter(job => {
      return (job.distanceInMiles || 0) <= NEARBY_RADIUS_MILES || job.isWithinGeofence;
    });

    setNearbyJobs(nearby);
    
    // Auto-select job if user is within its geofence
    console.log('🔍 Auto-select check:', {
      selectedJob: !!selectedJob,
      currentStatus,
      currentShopLocation: !!currentShopLocation,
      jobsWithDist: jobsWithDist.length
    });

    // Only auto-select when not clocked in OR when no job is selected in dropdown
    if (!selectedJob && currentStatus !== 'loading') {
      // Prefer shop job if at shop location
      if (currentShopLocation) {
        const shopJob = jobsWithDist.find(j => SHOP_JOB_IDS.includes(j.job_id));
        console.log('🏪 Looking for shop job:', { found: !!shopJob, shopJobIds: SHOP_JOB_IDS });
        if (shopJob) {
          console.log('✅ Auto-selecting shop job:', shopJob.job_name);
          setSelectedJob(shopJob);
          localStorage.setItem('current_job_id', shopJob.job_id.toString());
          console.log('🏪 Auto-selected shop job');
        } else {
          console.log('⚠️ Shop job not found in jobs list');
        }
      } else if (jobUserIsAt && currentStatus === 'not_clocked_in') {
        // How many NON-shop jobs is the user physically inside right now?
        const insideNonShop = jobsWithDist.filter(j =>
          j.isWithinGeofence && !SHOP_JOB_IDS.includes(j.job_id)
        );
        if (insideNonShop.length >= 2) {
          // Ambiguous — inside multiple overlapping sites. Don't auto-pick
          // the nearest (that's how wrong clock-ins happen). Prompt instead,
          // unless the user already dismissed it for this overlap.
          if (!multiSiteDismissedRef.current) {
            setMultiSiteOptions(insideNonShop);
            setShowMultiSitePrompt(true);
          }
        } else {
          // No longer ambiguous — allow the prompt to show again next time.
          multiSiteDismissedRef.current = false;
          if (!SHOP_JOB_IDS.includes(jobUserIsAt.job_id)) {
            // Exactly one field job — safe to auto-select.
            setSelectedJob(jobUserIsAt);
            localStorage.setItem('current_job_id', jobUserIsAt.job_id.toString());
          }
        }
      }
    }
  }, [location, jobs, selectedJob, currentStatus, currentShopLocation, t.milesAway, isUserWithinJobGeofence]);

  // API functions with timeout for weak signal handling
  const apiCall = async (endpoint: string, method = 'GET', body: any = null) => {
    const token = localStorage.getItem('bfa_token');

    // Check if token exists
    if (!token) {
      console.error('No authentication token found');
      // Redirect to login
      window.location.href = '/login';
      throw new Error('No authentication token');
    }

    // Create AbortController for timeout (7 seconds - fast fail on weak signal)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);

    const options: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      signal: controller.signal
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
      clearTimeout(timeoutId);
      const data = await response.json();
      
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        console.error('Token expired or invalid, redirecting to login...');
        // Clear stored data
        localStorage.removeItem('bfa_token');
        localStorage.removeItem('bfa_user');
        localStorage.removeItem('current_job_id');
        
        // Redirect to login page
        window.location.href = '/login';
        throw new Error('Authentication expired');
      }
      
      if (!response.ok) {
        throw new Error(data.error || `API request failed: ${response.status}`);
      }
      
      return data;
    } catch (err: any) {
      clearTimeout(timeoutId);
      // Better error message for timeout/network issues
      if (err.name === 'AbortError') {
        console.error('API call timed out:', endpoint);
        throw new Error('Request timed out - check your connection');
      }
      console.error('API call failed:', endpoint, err);
      throw err;
    }
  };

  const loadJobs = async () => {
    try {
      const response = await apiCall('/api/jobs');
      if (response.success && response.data) {
        console.log('✅ Jobs loaded:', response.data.length);

        // Ensure shop job exists
        const hasShopJob = response.data.some((j: Job) =>
          SHOP_JOB_IDS.includes(j.job_id) || j.job_name.toLowerCase().includes('shop')
        );

        if (!hasShopJob) {
          console.error('⚠️ Warning: No shop job found in system');
        }

        // Jobs with the "Clock-In Location" toggle off (Job Activity page) are
        // hidden from the clock-in flow entirely. (Restored — this filter was
        // added in fbdf334 and accidentally deleted by stale-checkout commit
        // 11c2c96 on 2026-05-30, the same commit that killed 5 other clock
        // features. Without it, jobs like Arthrex Commerce Park Phase 2 stay
        // clock-in-able even with the toggle off.)
        const geofenceJobs = response.data.filter((j: Job) => j.geofence_enabled !== false);
        setJobs(geofenceJobs);
        setLoadingStates(prev => ({ ...prev, jobs: false }));
      } else {
        console.error('Failed to load jobs - no data in response');
        setJobs([]); // Set empty array to prevent infinite loading
        setCurrentStatus('not_clocked_in'); // Prevent infinite loading
        setLoadingStates(prev => ({ ...prev, jobs: false }));
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
      // Don't show error modal - let offline mode UI handle it
      setJobs([]); // Set empty array to prevent infinite loading
      setCurrentStatus('not_clocked_in'); // Prevent infinite loading
      setLoadingStates(prev => ({ ...prev, jobs: false }));
    }
  };

  // Fetch unknown job clock-in setting (public endpoint - no auth required)
  const loadUnknownJobSetting = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/public/allow-unknown-job-clockin`);
      const data = await response.json();
      if (data.success && data.data) {
        setAllowUnknownJobClockin(data.data.enabled || false);
        console.log('📍 Unknown job clock-in setting:', data.data.enabled ? 'ENABLED' : 'disabled');
      }
    } catch (err) {
      console.error('Failed to load unknown job setting:', err);
      // Default to disabled if we can't fetch the setting
      setAllowUnknownJobClockin(false);
    }
  };

  // Fetch timecard approval settings (public endpoint - no auth required)
  const loadTimecardApprovalSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/public/timecard-approval`);
      const data = await response.json();
      if (data.success && data.data) {
        setTimecardApprovalEnabled(data.data.enabled || false);
        setTimecardApprovalMandatory(data.data.mandatory || false);
        setTimecardApprovalTestMode(data.data.test_mode || false);
        console.log('📋 Timecard approval settings:', data.data.enabled ? 'ENABLED' : 'disabled', data.data.mandatory ? '(mandatory)' : '(optional)', data.data.test_mode ? '(TEST MODE)' : '');
      }
    } catch (err) {
      console.error('Failed to load timecard approval settings:', err);
      // Default to disabled if we can't fetch the setting
      setTimecardApprovalEnabled(false);
      setTimecardApprovalMandatory(false);
      setTimecardApprovalTestMode(false);
    }
  };

  // Check if Friday timecard review should be shown before clock-in
  const checkFridayTimecardReview = async (): Promise<boolean> => {
    // Only proceed if feature is enabled
    if (!timecardApprovalEnabled) {
      console.log('📋 Timecard approval feature is disabled');
      return false;
    }

    // The pay week ends Thursday and is normally reviewed Friday (approved over
    // the weekend). Show the review on ANY clock-in from Friday through Monday
    // 8:00 AM Eastern, so a holiday Friday (nobody clocked in) is still caught.
    // Computed in Eastern so the window is identical on every device/timezone.
    // Unless test_mode is enabled, then allow any day.
    const nowET = easternNow();
    const dow = nowET.getDay(); // 0=Sun .. 5=Fri .. 6=Sat
    const inReviewWindow = dow === 5 || dow === 6 || dow === 0 || (dow === 1 && nowET.getHours() < 8);
    if (!inReviewWindow && !timecardApprovalTestMode) {
      console.log('📋 Outside Fri–Mon 8am review window, skipping timecard review check');
      return false;
    }
    if (timecardApprovalTestMode) {
      console.log('📋 Test mode enabled - bypassing Friday check');
    }

    // Only check if employee is not clocked in (about to clock in)
    if (currentStatus !== 'not_clocked_in') {
      console.log('📋 Already clocked in, skipping timecard review check');
      return false;
    }

    console.log('📋 Friday clock-in detected, checking review status...');

    try {
      // Check if review has already been done for this week
      const statusResult = await apiCall('/api/timecard-review/status');

      if (!statusResult.success) {
        console.error('Failed to check review status:', statusResult.message);
        return false;
      }

      const { should_show_modal, week_start_date, week_end_date, review_status, entry_count, total_hours } = statusResult.data;

      if (!should_show_modal) {
        console.log('📋 Review already completed for this week');
        return false;
      }

      // Fetch the entries for the week - this endpoint returns entries AND crew_average_clock_out
      const entriesResult = await apiCall(`/api/timecard-review/entries?week_start=${week_start_date}`);
      if (!entriesResult.success) {
        console.error('Failed to load review entries:', entriesResult.message);
        return false;
      }

      console.log('📋 Entries result:', entriesResult.data);

      // Extract entries array and crew average from the response
      const entriesData = entriesResult.data?.entries || [];
      const crewAverage = entriesResult.data?.crew_average_clock_out || '17:00';

      // Set the review data and show modal
      // Ensure total_hours is a number before calling toFixed
      const hours = typeof total_hours === 'number' ? total_hours : parseFloat(total_hours) || 0;
      setTimecardReviewData({
        weekStartDate: week_start_date,
        weekEndDate: week_end_date,
        entries: entriesData,
        totalHours: hours.toFixed(2),
        crewAverageClockOut: crewAverage,
        canSkip: !timecardApprovalMandatory
      });

      setShowTimecardReview(true);
      return true;

    } catch (err) {
      console.error('Error checking Friday timecard review:', err);
      return false;
    }
  };

  // Handle timecard review approval
  const handleTimecardApprove = async () => {
    setShowTimecardReview(false);
    setTimecardReviewData(null);
    // After approval, proceed to show labor code selector
    setShowLaborCodeSelector(true);
  };

  // Handle timecard review skip
  const handleTimecardSkip = async () => {
    setShowTimecardReview(false);
    setTimecardReviewData(null);
    // After skip, proceed to show labor code selector
    setShowLaborCodeSelector(true);
  };

  // Handle timecard review close (X button)
  const handleTimecardClose = () => {
    if (!timecardApprovalMandatory) {
      setShowTimecardReview(false);
      setTimecardReviewData(null);
    }
    // If mandatory, user cannot close without approving or submitting corrections
  };

  // Handle photo capture for Unknown job clock-in
  const handleUnknownJobPhotoCapture = async (imageDataUrl: string) => {
    isCapturingPhotoRef.current = false;
    setShowUnknownJobCamera(false);
    setUnknownJobPhoto(imageDataUrl);

    // Convert data URL to File for upload
    const response = await fetch(imageDataUrl);
    const blob = await response.blob();
    const file = new File([blob], `unknown_job_${Date.now()}.jpg`, { type: 'image/jpeg' });
    setUnknownJobPhotoFile(file);

    // Now complete the clock-in with photo
    await completeUnknownJobClockIn(file);
  };

  // Use native camera for Unknown job photo
  const handleTakeUnknownJobPhoto = async () => {
    try {
      await openNativeCamera(async (file: File | File[]) => {
        const photoFile = Array.isArray(file) ? file[0] : file;
        const dataUrl = await fileToDataUrl(photoFile);
        setUnknownJobPhoto(dataUrl);
        setUnknownJobPhotoFile(photoFile);
        isCapturingPhotoRef.current = false;
        // Don't close modal yet - keep it open to show upload progress
        // setShowUnknownJobCamera(false) will be called after successful upload

        // Complete clock-in with photo (modal stays open during upload)
        await completeUnknownJobClockIn(photoFile);
      });
    } catch (err) {
      console.error('Failed to capture photo:', err);
      setError(language === 'es' ? 'Error al capturar foto' : 'Failed to capture photo');
      setShowUnknownJobCamera(false);
    }
  };

  // Complete Unknown job clock-in after photo is captured
  const completeUnknownJobClockIn = async (photoFile: File) => {
    if (!pendingUnknownJobClockIn) return;

    const { jobToClockIn, laborCode, shopWorkAllDay, isTemporary, lat, lng } = pendingUnknownJobClockIn;
    const t = getTranslations(language)[language];

    setUploadingPhoto(true);
    setLoading(true);

    try {
      // Upload photo first
      const formData = new FormData();
      formData.append('photo', photoFile);

      const uploadResponse = await fetch(`${API_BASE_URL}/api/time/upload-clock-in-photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        },
        body: formData
      });

      const uploadData = await uploadResponse.json();

      if (!uploadData.success) {
        throw new Error(uploadData.error || 'Failed to upload photo');
      }

      console.log('📸 Photo uploaded:', uploadData.data.photo_url);

      // Now clock in with the photo URL
      const clockInResponse = await apiCall('/api/time/clock-in', 'POST', {
        lat,
        lng,
        job_id: jobToClockIn.job_id,
        labor_code: laborCode,
        clocked_at_shop: false,
        shop_work_all_day: shopWorkAllDay,
        clock_in_photo_url: uploadData.data.photo_url
      });

      if (clockInResponse.success) {
        console.log('✅ Unknown job clock-in successful with photo!');

        // Set the Unknown job as the selected job
        setSelectedJob(jobToClockIn);
        setCurrentStatus('clocked_in');
        setShowLaborCodeSelector(false);

        const now = new Date();
        const timeStr = now.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        setClockInConfirmDetails({
          jobName: jobToClockIn.job_name,
          time: timeStr
        });

        // Close the camera modal now that upload is complete
        setShowUnknownJobCamera(false);

        // Show confirmation modal
        setShowClockInConfirmation(true);

        // Clear pending state and photo
        setPendingUnknownJobClockIn(null);
        setUnknownJobPhoto(null);
        setUnknownJobPhotoFile(null);

        // Refresh status from server
        await Promise.all([
          checkCurrentClockStatus(),
          loadWeeklyHours()
        ]);

        showSuccess(language === 'es' ? '¡Entrada registrada!' : 'Clocked in!');
      } else {
        throw new Error(clockInResponse.error || 'Clock-in failed');
      }
    } catch (err: any) {
      console.error('Unknown job clock-in error:', err);
      setError(err.message || (language === 'es' ? 'Error al registrar entrada' : 'Failed to clock in'));
      // Close modal on error too
      setShowUnknownJobCamera(false);
    } finally {
      setUploadingPhoto(false);
      setLoading(false);
    }
  };

  // Cancel Unknown job clock-in
  const handleCancelUnknownJobClockIn = () => {
    isCapturingPhotoRef.current = false;
    setShowUnknownJobCamera(false);
    setPendingUnknownJobClockIn(null);
    setUnknownJobPhoto(null);
    setUnknownJobPhotoFile(null);
  };

  // Load crew average clock out time and check for EXIT events for smart clock-out
  const loadCrewAverage = async () => {
    // First, check for EXIT events from background geolocation tracking
    try {
      const lastExitEvent = await backgroundGeolocationService.getLastExitFromCurrentJob();
      if (lastExitEvent) {
        // Convert the exit time to local time and 24-hour format
        const exitDate = new Date(lastExitEvent.eventTime);
        const hours = exitDate.getHours().toString().padStart(2, '0');
        const minutes = exitDate.getMinutes().toString().padStart(2, '0');
        const timeStr24 = `${hours}:${minutes}`;
        const timeStr12 = exitDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

        setSuggestedExitTime({
          time: timeStr24,
          jobName: lastExitEvent.jobName,
          eventTime: timeStr12
        });
        setForgotClockOutMethod('location_data');
        console.log(`📍 Found EXIT event: left ${lastExitEvent.jobName} at ${timeStr12}`);
      } else {
        // No exit event found
        setSuggestedExitTime(null);
        console.log('📍 No EXIT event found for smart clock-out');
      }
    } catch (err) {
      console.error('Error checking for EXIT events:', err);
      setSuggestedExitTime(null);
    }

    // Set intelligent default manual time based on clock-in time, but only if user hasn't changed it
    const clockInTime = openEntry ? new Date(openEntry.clock_in) : (currentEntry ? new Date(currentEntry.clock_in) : null);
    if (clockInTime && manualClockOutTime === '16:30') { // Only update if still at default
      // Default to 1 hour after clock-in time, but at least 4:30 PM
      const defaultClockOut = new Date(clockInTime.getTime() + 60 * 60 * 1000); // +1 hour
      const minTime = new Date(clockInTime);
      minTime.setHours(16, 30, 0, 0); // 4:30 PM minimum

      const useTime = defaultClockOut > minTime ? defaultClockOut : minTime;
      const hours = useTime.getHours().toString().padStart(2, '0');
      const minutes = useTime.getMinutes().toString().padStart(2, '0');
      console.log(`Setting default manual time to: ${hours}:${minutes}`);
      setManualClockOutTime(`${hours}:${minutes}`);
    } else if (clockInTime) {
      console.log(`Keeping user's manual time: ${manualClockOutTime}`);
    }

    if (!user?.employee_id || !selectedJob?.job_id || !user.crew_foreman_id) {
      setCrewAverageTime(null); // No default - force manual entry
      setForgotClockOutMethod('manual'); // Switch to manual if no crew data
      return;
    }

    try {
      // Get today's date in local timezone (not UTC)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;

      const response = await apiCall(
        `/api/time/crew-average?job_id=${selectedJob.job_id}&date=${today}&crew_id=${user.crew_foreman_id}`
      );

      if (response.success && response.data && response.data.average_clock_out && !response.data.using_default) {
        // Convert military time to 12-hour format
        const time = response.data.average_clock_out;
        const [hours, minutes] = time.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        setCrewAverageTime(`${hour12}:${minutes} ${ampm}`);
      } else {
        // No crew average available - force manual entry
        setCrewAverageTime(null);
        setForgotClockOutMethod('manual');
      }
    } catch (err) {
      console.error('Crew average endpoint error:', err);
      setCrewAverageTime(null);
      setForgotClockOutMethod('manual'); // Force manual if API fails
    }
  };

  const checkCurrentClockStatus = async () => {
    if (!user?.employee_id) {
      setLoadingStates(prev => ({ ...prev, clockStatus: false }));
      return;
    }

    try {
      const response = await apiCall(`/api/time?employee_id=${user.employee_id}&t=${Date.now()}`);
      if (response.success) {
        // Successfully connected - clear offline flags
        console.log('✅ Clock status check SUCCESS - clearing usingCachedStatus');
        setIsOffline(false);
        setUsingCachedStatus(false);

        const today = new Date().toDateString();

        // Check for ANY open entries (not just yesterday)
        const openEntries = response.data
          .filter((entry: TimeEntry) => !entry.clock_out)
          .sort((a: TimeEntry, b: TimeEntry) =>
            new Date(b.clock_in).getTime() - new Date(a.clock_in).getTime()
          );

        // If there's an open entry that's NOT from today, show the modal
        const oldOpenEntry = openEntries.find((entry: TimeEntry) =>
          new Date(entry.clock_in).toDateString() !== today
        );

        if (oldOpenEntry) {
          setOpenEntry(oldOpenEntry);
          setShowOpenEntryModal(true);
          setCurrentStatus('not_clocked_in'); // Set status so UI isn't stuck on loading
          setLoadingStates(prev => ({ ...prev, clockStatus: false })); // Clear loading state so modal is visible
          console.log('⚠️ Found open entry from:', new Date(oldOpenEntry.clock_in).toDateString());
          // Don't proceed with checking current status until this is resolved
          return;
        }

        // Check current status (today's entries only)
        const todayEntries = response.data
          .filter((entry: TimeEntry) => new Date(entry.clock_in).toDateString() === today)
          .sort((a: TimeEntry, b: TimeEntry) =>
            new Date(b.clock_in).getTime() - new Date(a.clock_in).getTime()
          );

        // Quarter-hour rounding can give several of the day's entries the SAME
        // clock_in timestamp, so sorting by clock_in leaves ties in an undefined
        // order and todayEntries[0] may be a CLOSED entry — which showed
        // "not clocked in" while an open entry existed. Pick the open entry.
        const openTodayEntry = todayEntries.find((e: TimeEntry) => !e.clock_out);
        const currentlyClocked = !!openTodayEntry;

        if (currentlyClocked && openTodayEntry) {
          const entry = openTodayEntry;
          setCurrentStatus('clocked_in');
          setCurrentEntry(entry);

          // Determine shop clock status BEFORE setting state (to cache correct values)
          const isTemporaryShop = entry.clocked_at_shop && !entry.shop_work_all_day;
          const isShopAllDay = entry.clocked_at_shop && entry.shop_work_all_day;
          const isClockedAtShop = isTemporaryShop || isShopAllDay;

          // Check if this is a temporary shop clock
          if (isTemporaryShop) {
            setClockedInAtShop(true);
            setIsTemporaryShopClock(true);
            setShopClockInTime(new Date(entry.clock_in));
            console.log('🏪 Temporary shop clock detected');
          } else if (isShopAllDay) {
            setClockedInAtShop(true);
            setIsTemporaryShopClock(false);
            console.log('🏭 Shop work all day mode');
          }

          // Cache the clock status for offline use (use computed values, not state)
          const cacheData = {
            currentStatus: 'clocked_in' as const,
            currentEntry: entry,
            clockedInAtShop: isClockedAtShop,
            isTemporaryShopClock: isTemporaryShop,
            shopClockInTime: isTemporaryShop ? new Date(entry.clock_in).toISOString() : null
          };
          offlineService.saveClockStatus(user.employee_id, cacheData);
          console.log('💾 Cached clock status:', cacheData);

          // Check if forgot button should show (after 7 PM)
          const now = new Date();
          setShowForgotButton(now.getHours() >= LATE_SHIFT_REMINDER_HOUR);

          // Update background service with entry_id and employee_id for geofence event logging
          if (entry.entry_id && user?.employee_id) {
            backgroundGeolocationService.setEntryId(entry.entry_id);
            backgroundGeolocationService.setEmployeeId(user.employee_id);
            console.log('📍 Updated background service with entry_id:', entry.entry_id);
          }

          // Set selected job based on time entry when clocked in
          if (entry.job_id && jobs.length > 0) {
            const clockedJob = jobs.find(j => j.job_id === entry.job_id);
            if (clockedJob && location) {
              // Calculate if user is actually within geofence
              const distanceInMeters = calculateDistance(
                location?.latitude,
                location?.longitude,
                clockedJob.lat,
                clockedJob.lng
              );
              const distanceInMiles = metersToMiles(distanceInMeters);
              const isWithinGeofence = isUserWithinJobGeofence(clockedJob, location?.latitude, location?.longitude);

              // Convert to JobWithDistance format with accurate geofence status
              const jobWithDistance: JobWithDistance = {
                ...clockedJob,
                distance: isWithinGeofence ? 'At Location' : `${distanceInMiles.toFixed(2)} miles away`,
                distanceValue: distanceInMeters,
                distanceInMiles: distanceInMiles,
                isWithinGeofence: isWithinGeofence
              };
              setSelectedJob(jobWithDistance);
              localStorage.setItem('current_job_id', clockedJob.job_id.toString());
              console.log('✅ Set selected job from time entry:', clockedJob.job_name, 'Within geofence:', isWithinGeofence);

              // Debug: Check all jobs for potential conversion opportunities
              console.log('🔍 Checking all jobs for conversion opportunities:');
              jobs.forEach(job => {
                const jobDistance = calculateDistance(location?.latitude, location?.longitude, job.lat, job.lng);
                const jobInGeofence = isUserWithinJobGeofence(job, location?.latitude, location?.longitude);
                console.log(`- ${job.job_name} (ID: ${job.job_id}): ${metersToMiles(jobDistance).toFixed(2)} miles, geofence: ${jobInGeofence}`);
              });
            }
          }
          console.log('✅ Clock status checked - clocked into job_id:', entry.job_id);
        } else {
          setCurrentStatus('not_clocked_in');
          setClockedInAtShop(false);
          setIsTemporaryShopClock(false);
          setShopClockInTime(null);
          setCurrentEntry(null);
          setShowForgotButton(false);

          // Cache the not_clocked_in status
          const cacheData = {
            currentStatus: 'not_clocked_in' as const,
            currentEntry: null,
            clockedInAtShop: false,
            isTemporaryShopClock: false,
            shopClockInTime: null
          };
          offlineService.saveClockStatus(user.employee_id, cacheData);
          console.log('💾 Cached clock status (not clocked in):', cacheData);
        }
      }
      setLoadingStates(prev => ({ ...prev, clockStatus: false }));
    } catch (err) {
      console.error('Failed to check clock status:', err);

      // Try to use cached status (Option 3: Hybrid approach)
      const cached = offlineService.getCachedClockStatus(user.employee_id);

      if (cached) {
        // Use cached status with warning
        console.log('📱 Using cached clock status (offline)', cached);
        setIsOffline(true);
        setUsingCachedStatus(true);
        setCurrentStatus(cached.currentStatus);
        // Cast cached entry to match local TimeEntry type (which includes id field)
        const entry = (cached.currentEntry as unknown) as TimeEntry | null;
        setCurrentEntry(entry);
        setClockedInAtShop(cached.clockedInAtShop);
        setIsTemporaryShopClock(cached.isTemporaryShopClock);
        if (cached.shopClockInTime) {
          setShopClockInTime(new Date(cached.shopClockInTime));
        }

        // Restore selected job from cached entry
        if (entry && entry.job_id && jobs.length > 0) {
          const clockedJob = jobs.find(j => j.job_id === entry.job_id);
          if (clockedJob) {
            const jobWithDistance: JobWithDistance = {
              ...clockedJob,
              distance: 'Cached',
              distanceValue: 0,
              distanceInMiles: 0,
              isWithinGeofence: false
            };
            setSelectedJob(jobWithDistance);
            console.log('✅ Restored selected job from cache:', clockedJob.job_name);
          }
        }
      } else {
        // No cached status - stay on loading screen with error
        console.log('❌ No cached status available - cannot verify clock status');
        setIsOffline(true);
        setUsingCachedStatus(false);
        // Keep currentStatus as 'loading' to show the no service screen
      }

      setLoadingStates(prev => ({ ...prev, clockStatus: false }));
    }
  };

  const loadWeeklyHours = async () => {
    if (!user?.employee_id) return;
    
    try {
      const today = new Date();
      const dayOfWeek = today.getDay();
      
      // Calculate days since last Friday
      let daysSinceLastFriday;
      if (dayOfWeek >= 5) {
        daysSinceLastFriday = dayOfWeek - 5;
      } else {
        daysSinceLastFriday = dayOfWeek + 2;
      }
      
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - daysSinceLastFriday);
      weekStart.setHours(0, 0, 0, 0);
      
      const weekDays: WeekDay[] = [];
      const dayNames = ['Friday', 'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];
      const dayNamesShort = language === 'es' ? 
        ['Vie', 'Sáb', 'Dom', 'Lun', 'Mar', 'Mié', 'Jue'] :
        ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + i);
        
        weekDays.push({
          date: formatDateForComparison(date),
          displayDate: `${date.getMonth() + 1}/${date.getDate()}`,
          dayName: dayNames[i],
          dayShort: dayNamesShort[i],
          hours: 0
        });
      }
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      
      const response = await apiCall(
        `/api/time?employee_id=${user.employee_id}&start_date=${weekStart.toISOString()}&end_date=${weekEnd.toISOString()}&_t=${Date.now()}`
      );
      
      if (response.success && response.data) {
        let total = 0;
        
        response.data.forEach((entry: TimeEntry) => {
          if (!entry.clock_out) return;
          
          let hoursWorked = 0;
          
          if (entry.hours_worked !== undefined && entry.hours_worked !== null) {
            // Always trust the backend's calculated hours_worked which includes rounding
            hoursWorked = Math.abs(parseFloat(entry.hours_worked.toString()));
          } else if (entry.clock_out && entry.clock_in) {
            // Fallback calculation only if hours_worked is not available
            const clockIn = new Date(entry.clock_in);
            const clockOut = new Date(entry.clock_out);
            const totalMinutes = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60);
            const lunchMin = entry.lunch_minutes || 0;
            hoursWorked = Math.max(0, (totalMinutes - lunchMin) / 60);
          }
          
          const clockInDate = new Date(entry.clock_in);
          const entryDate = formatDateForComparison(clockInDate);
          
          const dayIndex = weekDays.findIndex(d => d.date === entryDate);
          
          if (dayIndex !== -1) {
            weekDays[dayIndex].hours += hoursWorked;
            total += hoursWorked;
          }
        });
        
        setWeeklyHours(weekDays);
        setTotalWeekHours(Math.abs(total));
      }
    } catch (err) {
      console.error('Failed to load weekly hours:', err);
    }
  };


  const checkForAutoConvert = (userLocation: Location) => {
    if (!isTemporaryShopClock || !allJobsWithDistance.length) return;

    const jobUserIsAt = allJobsWithDistance.find(job => {
      // Allow conversion to different shop jobs OR field jobs
      // Just exclude the current job
      if (selectedJob && job.job_id === selectedJob.job_id) return false;
      return isUserWithinJobGeofence(job, userLocation.lat, userLocation.lng);
    });

    if (jobUserIsAt && !showConvertPrompt) {
      console.log('📍 Entered job geofence, prompting to convert:', jobUserIsAt.job_name);
      setTargetJob(jobUserIsAt);
      setShowConvertPrompt(true);
    }
  };

  // Simple check for forgot button visibility (after 7 PM)
  // Only shows once per day - can be dismissed
  const checkForgotButtonVisibility = () => {
    if (currentStatus === 'clocked_in' && currentEntry) {
      const now = new Date();
      const hour = now.getHours();
      const todayKey = `forgot_dismissed_${now.toDateString()}`;
      const wasDismissedToday = localStorage.getItem(todayKey) === 'true';

      // Only show if after 7 PM AND not already dismissed today
      setShowForgotButton(hour >= LATE_SHIFT_REMINDER_HOUR && !wasDismissedToday);
    } else {
      setShowForgotButton(false);
    }
  };

  // Dismiss the forgot button for today
  const dismissForgotButton = () => {
    const todayKey = `forgot_dismissed_${new Date().toDateString()}`;
    localStorage.setItem(todayKey, 'true');
    setShowForgotButton(false);
  };

  // Handler for shop work confirmation
  const handleShopWorkConfirm = (isAllDay: boolean) => {
    console.log('🏪 Shop work confirmation:', isAllDay ? 'All Day' : 'Temporary');
    setShowShopWorkConfirm(false);

    if (isAllDay) {
      // User confirmed they are working at shop all day
      console.log('✅ Setting shopWorkConfirmed=true and calling handleClockIn');
      setShopWorkConfirmed(true); // Set flag to prevent re-showing confirmation
      handleClockIn('SHOP');
    } else {
      // User wants temporary shop clock - show labor codes again with other codes
      console.log('📋 Showing labor code selector again');
      setShowLaborCodeSelector(true);
    }
  };

  // Clock operations
  // Handle continue after clock-in - check for special requirements first.
  // (Restored — deleted by stale-checkout commit 11c2c96 on 2026-05-30. The
  // /special-requirements page, route, and backend endpoints survived intact.)
  const handleClockInContinue = async () => {
    // Only admin and foreman need to see special requirements
    if (user?.role !== 'admin' && user?.role !== 'foreman') {
      setShowClockInConfirmation(false);
      setClockInConfirmDetails(null);
      navigate('/');
      return;
    }

    // Get the job ID from the current entry or selected job
    const jobId = currentEntry?.job_id || selectedJob?.job_id;
    if (!jobId) {
      setShowClockInConfirmation(false);
      setClockInConfirmDetails(null);
      navigate('/');
      return;
    }

    try {
      setCheckingSpecialRequirements(true);

      // First check if the feature is enabled in settings (defaults to OFF)
      const settingsResponse = await fetch(`${API_BASE_URL}/api/settings/public/special-requirements-enabled`);
      const settingsData = await settingsResponse.json();

      if (!settingsData.success || !settingsData.data?.enabled) {
        // Feature is disabled, go straight to dashboard
        setShowClockInConfirmation(false);
        setClockInConfirmDetails(null);
        navigate('/');
        return;
      }

      // Feature is enabled, check if this job/user needs to see the page
      const response = await fetch(`${API_BASE_URL}/api/special-requirements/check/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });
      const data = await response.json();

      if (data.success && data.data?.showPage) {
        // Navigate to special requirements page
        setShowClockInConfirmation(false);
        setClockInConfirmDetails(null);
        navigate('/special-requirements', {
          state: {
            jobId: jobId,
            returnPath: '/'
          }
        });
      } else {
        // No special requirements, go to dashboard
        setShowClockInConfirmation(false);
        setClockInConfirmDetails(null);
        navigate('/');
      }
    } catch (err) {
      console.error('Error checking special requirements:', err);
      // On error, just go to dashboard
      setShowClockInConfirmation(false);
      setClockInConfirmDetails(null);
      navigate('/');
    } finally {
      setCheckingSpecialRequirements(false);
    }
  };

  const handleClockIn = async (laborCode: string) => {
    console.log('⏰ Clock in with code:', laborCode);
    console.log('🔍 State check:', {
      currentShopLocation: !!currentShopLocation,
      shopWorkConfirmed,
      location: !!location,
      selectedJob: !!selectedJob,
      allJobsWithDistance: allJobsWithDistance.length
    });

    // If user selected SHOP code at shop location, show confirmation first (unless already confirmed)
    if (laborCode === 'SHOP' && currentShopLocation && !shopWorkConfirmed) {
      console.log('📋 Showing shop work confirmation modal');
      setShowLaborCodeSelector(false);
      setShowShopWorkConfirm(true);
      return;
    }

    console.log('✅ Passed confirmation check, proceeding with clock-in');

    // Only check for double-tap AFTER confirmation dialogs
    if (preventDoubleTap()) {
      console.log('⏸️ Blocked by preventDoubleTap');
      return;
    }

    // Force fresh GPS check before clock-in to verify worker is at site
    console.log('📍 Forcing fresh GPS check for clock-in verification...');
    setLoading(true);
    const freshLocation = await forceRefreshLocation();

    if (!freshLocation) {
      console.log('❌ Could not get fresh location for clock-in');
      setLocationRetryStatus('idle');
      setLocationRetry({ action: 'in', laborCode });
      setLoading(false);
      return;
    }
    console.log('✅ Fresh location acquired:', freshLocation.latitude, freshLocation.longitude);
    setLoading(false);

    let jobToClockIn = selectedJob;
    let isTemporary = false;
    let shopWorkAllDay = false;

    // At shop logic
    if (currentShopLocation) {
      // First try to find job with "shop" in the name
      let shopJob = allJobsWithDistance.find(j =>
        j.job_name.toLowerCase().includes('shop') ||
        j.job_name.toLowerCase().includes('bradanna')
      );

      // Fallback to any shop job ID if no shop-named job found
      if (!shopJob) {
        shopJob = allJobsWithDistance.find(j => SHOP_JOB_IDS.includes(j.job_id));
      }

      if (!shopJob) {
        setError('Shop job not configured in system');
        return;
      }

      console.log('🏪 Using shop job for clock-in:', shopJob.job_name, 'ID:', shopJob.job_id);
      
      jobToClockIn = shopJob;
      
      if (laborCode === 'SHOP') {
        shopWorkAllDay = true;
        isTemporary = false;
      } else {
        shopWorkAllDay = false;
        isTemporary = true;
      }
    }
    
    // If no job selected but unknown job clock-in is enabled, go directly to Unknown job flow
    if (!jobToClockIn && allowUnknownJobClockin) {
      const unknownJob = allJobsWithDistance.find(j =>
        j.job_name.toLowerCase() === 'unknown'
      );

      if (unknownJob) {
        console.log('📍 No job selected, using Unknown job clock-in with photo');
        setPendingUnknownJobClockIn({
          jobToClockIn: unknownJob,
          laborCode,
          shopWorkAllDay: false,
          isTemporary: false,
          lat: freshLocation.latitude,
          lng: freshLocation.longitude
        });
        isCapturingPhotoRef.current = true;
        setShowUnknownJobCamera(true);
        return;
      } else {
        console.log('❌ Unknown job not found in system');
        setError(language === 'es' ? 'Trabajo "Unknown" no configurado' : 'Unknown job not configured in system');
        return;
      }
    }

    if (!jobToClockIn) {
      console.log('❌ No job selected');
      setError(t.selectJobFirst);
      return;
    }

    // Must be within geofence or at shop - unless unknown job clock-in is enabled
    // Recheck with the fresh fix — isWithinGeofence was computed from the older
    // context location (clock-out already validates this way).
    // Small grace buffer applies only when this is the sole nearby job.
    const isAtJobWithFreshGPS = isUserWithinJobGeofence(
      jobToClockIn,
      freshLocation.latitude,
      freshLocation.longitude
    ) || (
      isSoleNearbyJob(jobToClockIn.job_id) &&
      isNearJobWithBuffer(jobToClockIn, freshLocation.latitude, freshLocation.longitude, CLOCK_IN_GEOFENCE_BUFFER_M)
    );
    if (!isAtJobWithFreshGPS && !currentShopLocation) {
      if (allowUnknownJobClockin) {
        // Find the "Unknown" job and use it instead
        const unknownJob = allJobsWithDistance.find(j =>
          j.job_name.toLowerCase() === 'unknown'
        );

        if (unknownJob) {
          console.log('📍 Outside geofence but unknown job clock-in enabled - requiring photo');
          // Store pending clock-in details and show camera
          setPendingUnknownJobClockIn({
            jobToClockIn: unknownJob,
            laborCode,
            shopWorkAllDay,
            isTemporary,
            lat: freshLocation.latitude,
            lng: freshLocation.longitude
          });
          isCapturingPhotoRef.current = true;
          setShowUnknownJobCamera(true);
          return; // Wait for photo capture
        } else {
          console.log('❌ Unknown job not found in system');
          setError(language === 'es' ? 'Trabajo "Unknown" no configurado' : 'Unknown job not configured in system');
          return;
        }
      } else {
        console.log('❌ Not at job site or shop');
        // Log the geofence rejection (fire-and-forget). A failed clock-in leaves
        // NO time entry, so this is the only record of the attempt — surfaced on
        // the timecard at the employee+day level.
        // (Restored — this block was accidentally deleted by commit 11c2c96 on
        // 2026-05-30, which reverted ClockInOut.tsx to a pre-build-116 version.)
        try {
          const distToCenter = jobToClockIn
            ? calculateDistance(freshLocation.latitude, freshLocation.longitude, jobToClockIn.lat, jobToClockIn.lng)
            : null;
          apiCall('/api/time/clock-failures', 'POST', {
            job_id: jobToClockIn?.job_id ?? null,
            action: 'clock_in',
            lat: freshLocation.latitude,
            lng: freshLocation.longitude,
            gps_accuracy_m: (freshLocation as any).accuracy ?? null,
            distance_m: distToCenter,
            geofence_radius_m: jobToClockIn?.geofence_data?.radius ?? null,
            reason: 'outside_geofence',
          }).catch(() => {});
        } catch (e) { /* never block the clock-in flow on logging */ }
        setError(t.mustBeAtSite);
        return;
      }
    }

    console.log('🚀 Sending clock-in request to API...', {
      job_id: jobToClockIn.job_id,
      labor_code: laborCode,
      clocked_at_shop: !!currentShopLocation,
      shop_work_all_day: shopWorkAllDay
    });

    setLoading(true);
    try {
      const response = await apiCall('/api/time/clock-in', 'POST', {
        lat: freshLocation.latitude,
        lng: freshLocation.longitude,
        job_id: jobToClockIn.job_id,
        labor_code: laborCode,
        clocked_at_shop: currentShopLocation ? true : false,
        shop_work_all_day: shopWorkAllDay
      });
      
      console.log('📡 API Response:', response);

      if (response.success) {
        console.log('✅ Clock-in successful!');
        setCurrentStatus('clocked_in');
        setShowLaborCodeSelector(false);
        setShopWorkConfirmed(false); // Reset confirmation flag

        // Format the current time
        const now = new Date();
        const timeStr = now.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        // Set confirmation details
        setClockInConfirmDetails({
          jobName: jobToClockIn.job_name,
          time: timeStr
        });

        // Show different success messages based on shop status
        if (currentShopLocation && isTemporary) {
          setClockedInAtShop(true);
          setIsTemporaryShopClock(true);
          setShopClockInTime(new Date());
        } else if (currentShopLocation && shopWorkAllDay) {
          setClockedInAtShop(true);
          setIsTemporaryShopClock(false);
        }

        // Run status checks in parallel for better performance
        await Promise.all([
          checkCurrentClockStatus(),
          loadWeeklyHours()
        ]);

        console.log('✅ Status checks complete');

        // Start background geolocation tracking for auto job switching
        // On native platforms, show disclosure first if user hasn't accepted yet (Google Play requirement)
        const hasAcceptedBackgroundLocation = localStorage.getItem(BACKGROUND_LOCATION_ACCEPTED_KEY) === 'true';

        if (Capacitor.isNativePlatform() && !hasAcceptedBackgroundLocation) {
          // Show disclosure dialog first, then start tracking after user accepts
          console.log('📍 Showing background location disclosure (first time)');
          setPendingBackgroundTrackingJobId(jobToClockIn.job_id);
          setShowBackgroundLocationDisclosure(true);
        } else if (Capacitor.isNativePlatform()) {
          // User already accepted, start tracking directly
          backgroundGeolocationService.startTracking(jobToClockIn.job_id).then(started => {
            if (started) {
              console.log('📍 Background geolocation tracking started');
            } else {
              console.log('📍 Background geolocation not available (permission denied)');
            }
          }).catch(err => {
            console.error('📍 Error starting background geolocation:', err);
          });
        } else {
          console.log('📍 Background geolocation not available (web platform)');
        }

        // Show confirmation modal instead of navigating immediately
        setShowClockInConfirmation(true);
      } else {
        console.log('❌ API returned success=false:', response);
      }
    } catch (err: any) {
      console.error('❌ Clock in error:', err);
      setError(err.message || (language === 'es' ? 'Error al registrar entrada' : 'Clock in failed'));
      setShopWorkConfirmed(false); // Reset flag on error too
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (preventDoubleTap()) return;

    if (!user?.employee_id || !currentEntry) return;

    // Force fresh GPS check before clock-out to verify worker is still at site
    console.log('📍 Forcing fresh GPS check for clock-out verification...');
    setLoading(true);
    const freshLocation = await forceRefreshLocation();

    if (!freshLocation) {
      console.log('❌ Could not get fresh location for clock-out');
      setLocationRetryStatus('idle');
      setLocationRetry({ action: 'out' });
      setLoading(false);
      return;
    }
    console.log('✅ Fresh location acquired:', freshLocation.latitude, freshLocation.longitude);
    setLoading(false);

    // IMPORTANT: Recalculate geofence status using FRESH coordinates
    // The allJobsWithDistance state may have stale data from before the fresh GPS check
    const freshShopLocation = SHOP_LOCATIONS.find(shop =>
      isInsideCircle(freshLocation.latitude, freshLocation.longitude, shop.lat, shop.lng, shop.radius)
    );

    // Clock-out is lenient: inside the fence OR within the grace buffer of it —
    // "still in the area" is enough to stop the clock.
    const isAtSelectedJobWithFreshGPS = selectedJob ? isNearJobWithBuffer(
      selectedJob,
      freshLocation.latitude,
      freshLocation.longitude,
      CLOCK_OUT_GEOFENCE_BUFFER_M
    ) : false;

    const atAnyJobSiteWithFreshGPS = jobs.some(job =>
      isNearJobWithBuffer(job, freshLocation.latitude, freshLocation.longitude, CLOCK_OUT_GEOFENCE_BUFFER_M)
    );

    // Debug logging for clock out validation
    console.log('Clock out validation check (using FRESH GPS):');
    console.log('- freshShopLocation:', freshShopLocation?.name || 'None');
    console.log('- selectedJob:', selectedJob?.job_name);
    console.log('- isAtSelectedJobWithFreshGPS:', isAtSelectedJobWithFreshGPS);
    console.log('- atAnyJobSiteWithFreshGPS:', atAnyJobSiteWithFreshGPS);
    console.log('- clockedInAtShop:', clockedInAtShop);
    console.log('- isTemporaryShopClock:', isTemporaryShopClock);

    // Validate location before allowing clock out
    // Allow clock out if:
    // 1. At a shop location (using fresh GPS), OR
    // 2. At the geofence of currently selected job (using fresh GPS), OR
    // 3. At the geofence of ANY active job (for shop workers who moved to job sites), OR
    // 4. Clocked into "Unknown" job AND within 0.5 miles of clock-in location
    // NOTE: Use currentEntry instead of selectedJob to handle archived jobs.
    // If job was archived, selectedJob becomes undefined but currentEntry still exists.
    console.log('🔍 Clock-out check - currentEntry job_name:', currentEntry?.job_name);
    const isClockedIntoUnknownJob = currentEntry?.job_name?.toLowerCase() === 'unknown';
    console.log('🔍 Is clocked into Unknown job:', isClockedIntoUnknownJob);

    // For Unknown job, check if within 0.5 miles of clock-in location
    let unknownJobClockOutAllowed = false;
    if (isClockedIntoUnknownJob && currentEntry?.clock_in_lat && currentEntry?.clock_in_lng) {
      const HALF_MILE_METERS = 804.672; // 0.5 miles in meters
      const distanceFromClockIn = calculateDistance(
        freshLocation.latitude,
        freshLocation.longitude,
        currentEntry.clock_in_lat,
        currentEntry.clock_in_lng
      );
      const distanceInMiles = distanceFromClockIn / 1609.34;
      console.log(`🔍 Unknown job clock-out distance check: ${distanceInMiles.toFixed(2)} miles from clock-in location`);

      if (distanceFromClockIn <= HALF_MILE_METERS) {
        unknownJobClockOutAllowed = true;
        console.log('✅ Clock out allowed - within 0.5 miles of clock-in location');
      } else {
        console.log(`❌ Clock out blocked: ${distanceInMiles.toFixed(2)} miles from clock-in location (max 0.5 miles)`);
        setError(language === 'es'
          ? `Debes estar a menos de 0.5 millas de donde fichaste entrada. Actualmente estás a ${distanceInMiles.toFixed(2)} millas.`
          : `You must be within 0.5 miles of where you clocked in. You are currently ${distanceInMiles.toFixed(2)} miles away.`);
        return;
      }
    } else if (isClockedIntoUnknownJob) {
      // No clock-in coordinates stored - allow clock out (legacy entries)
      unknownJobClockOutAllowed = true;
      console.log('✅ Clock out allowed - Unknown job with no stored clock-in coordinates');
    }

    if (!freshShopLocation && currentEntry && !isAtSelectedJobWithFreshGPS && !atAnyJobSiteWithFreshGPS && !unknownJobClockOutAllowed) {
      console.log('❌ Clock out blocked: Not at job site or shop (verified with fresh GPS)');
      console.log('   - Job may have been archived. User must be at shop or another active job site.');
      // Log the geofence rejection (fire-and-forget) so admins can diagnose it on
      // the timecard — position, distance to the job, and the fence radius.
      // (Restored — this block was accidentally deleted by commit 11c2c96 on
      // 2026-05-30, which reverted ClockInOut.tsx to a pre-build-116 version.)
      try {
        const distToCenter = selectedJob
          ? calculateDistance(freshLocation.latitude, freshLocation.longitude, selectedJob.lat, selectedJob.lng)
          : null;
        apiCall('/api/time/clock-failures', 'POST', {
          job_id: currentEntry?.job_id ?? selectedJob?.job_id ?? null,
          action: 'clock_out',
          lat: freshLocation.latitude,
          lng: freshLocation.longitude,
          gps_accuracy_m: (freshLocation as any).accuracy ?? null,
          distance_m: distToCenter,
          geofence_radius_m: selectedJob?.geofence_data?.radius ?? null,
          reason: 'outside_geofence',
        }).catch(() => {});
      } catch (e) { /* never block the clock-out flow on logging */ }
      setError(t.mustBeAtJobSite);
      return;
    }

    console.log('✅ Clock out validation passed');

    // Check daily report requirement for foremen
    if (user.role === 'foreman' || user.role === 'asphalt_foreman') {
      try {
        // Fetch daily report requirement setting
        const settingResponse = await apiCall('/api/settings/daily-report-requirement');
        if (settingResponse.success && settingResponse.data.enabled) {
          const { minHours } = settingResponse.data;

          // Calculate hours worked at current job
          const clockInTime = new Date(currentEntry.clock_in);
          const now = new Date();
          const hoursWorked = (now.getTime() - clockInTime.getTime()) / (1000 * 60 * 60);

          console.log(`📋 Daily report check: ${hoursWorked.toFixed(2)} hours worked (min: ${minHours})`);

          // If worked enough hours, check if report was submitted
          // (Night-shift-aware version restored — the today-only local-date check
          // below it was a regression from stale-checkout commit 11c2c96, which
          // wrongly blocked foremen clocking out after midnight and used the
          // phone's local timezone instead of Eastern.)
          if (hoursWorked >= minHours) {
            // Get clock-in date in Eastern time
            const clockInUtc = new Date(currentEntry.clock_in);
            const clockInDateStr = clockInUtc.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD format

            // Get today's date in Eastern time
            const todayDateStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

            // Determine if this is a night shift (clocked in on a different day than today)
            const isNightShift = clockInDateStr !== todayDateStr;

            const jobId = currentEntry.job_id;

            console.log(`📋 Checking for daily report:`, {
              clockInDateStr,
              todayDateStr,
              isNightShift,
              jobId,
              userEmployeeId: user.employee_id
            });

            // Helper function to check for reports on a specific date.
            // Checks BOTH endpoints regardless of role — regular foremen file to
            // DAILY_REPORTS, asphalt foremen to ASPHALT_DAILY_REPORTS, and either
            // counts (one foreman's report unblocks everyone on the job).
            const checkReportForDate = async (dateStr: string): Promise<boolean> => {
              // Check daily reports
              const apiUrl = `/api/daily-reports?date=${dateStr}&job_id=${jobId}`;
              const reportResponse = await apiCall(apiUrl);

              if (reportResponse.success && reportResponse.data && reportResponse.data.length > 0) {
                console.log(`📋 Daily report found for ${dateStr}`);
                return true;
              }

              // Check asphalt reports
              const asphaltApiUrl = `/api/asphalt-reports?start_date=${dateStr}&end_date=${dateStr}&job_id=${jobId}`;
              const asphaltResponse = await apiCall(asphaltApiUrl);

              if (asphaltResponse.success && asphaltResponse.data && asphaltResponse.data.length > 0) {
                console.log(`📋 Asphalt report found for ${dateStr}`);
                return true;
              }

              return false;
            };

            let hasSubmittedReport = false;

            // Check all reasonable dates: clock-in date, today, and tomorrow
            // Reports can land on different dates depending on night work window,
            // so just check them all rather than guessing which date it's on
            const datesToCheck = new Set<string>();
            datesToCheck.add(clockInDateStr);
            datesToCheck.add(todayDateStr);

            // Add tomorrow's date (covers night reports dated for next day)
            const tomorrowEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
            tomorrowEST.setDate(tomorrowEST.getDate() + 1);
            datesToCheck.add(tomorrowEST.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));

            for (const dateStr of datesToCheck) {
              if (hasSubmittedReport) break;
              console.log(`📋 Checking for report on ${dateStr}...`);
              hasSubmittedReport = await checkReportForDate(dateStr);
            }

            if (!hasSubmittedReport) {
              console.log('❌ Clock out blocked: Daily report required');
              setError(
                language === 'es'
                  ? `Debes enviar un reporte diario antes de salir (has trabajado ${hoursWorked.toFixed(1)} horas)`
                  : `You must submit a daily report before clocking out (you've worked ${hoursWorked.toFixed(1)} hours)`
              );
              return;
            } else {
              console.log('✅ Report submitted (daily or asphalt), allowing clock out');
            }
          }
        }
      } catch (err) {
        console.error('Error checking daily report requirement:', err);
        // Don't block clock out if the check fails (fail open)
      }
    }

    // Check block photo-only clockout setting for foremen
    // (Restored — this block was accidentally deleted by commit 11c2c96 on
    // 2026-05-30. The admin toggle in bfav5 Settings has existed all along.)
    if (user.role === 'foreman' || user.role === 'asphalt_foreman') {
      try {
        const photoOnlyResponse = await apiCall('/api/settings/public/block-photo-only-clockout');
        if (photoOnlyResponse.success && photoOnlyResponse.data.enabled) {
          // Same hours floor as the daily-report rule above (default 4). This
          // block had no threshold, so a 20-minute clock-in demanded a work
          // description just like a full day did (2026-10-30).
          let photoMinHours = 4;
          try {
            const req = await apiCall('/api/settings/daily-report-requirement');
            if (req.success && typeof req.data?.minHours === 'number') photoMinHours = req.data.minHours;
          } catch { /* keep default */ }
          const photoHoursWorked = (Date.now() - new Date(currentEntry.clock_in).getTime()) / (1000 * 60 * 60);
          if (photoHoursWorked < photoMinHours) {
            console.log(`📋 Photo-only check skipped: ${photoHoursWorked.toFixed(2)} h worked (min: ${photoMinHours})`);
          } else {
          const jobId = currentEntry.job_id;

          // Check all reasonable dates (clock-in, today, tomorrow) for a real report
          const clockInUtc = new Date(currentEntry.clock_in);
          const photoCheckDates = new Set<string>();
          photoCheckDates.add(clockInUtc.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
          photoCheckDates.add(new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
          const tmrw = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
          tmrw.setDate(tmrw.getDate() + 1);
          photoCheckDates.add(tmrw.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));

          let foundRealReport = false;
          for (const reportDate of photoCheckDates) {
            // Check asphalt reports first - always have real content
            const asphaltUrl = `/api/asphalt-reports?start_date=${reportDate}&end_date=${reportDate}&job_id=${jobId}`;
            const asphaltResponse = await apiCall(asphaltUrl);
            if (asphaltResponse.success && asphaltResponse.data && asphaltResponse.data.length > 0) {
              console.log(`✅ Asphalt report found on ${reportDate}, skipping photo-only check`);
              foundRealReport = true;
              break;
            }

            // Check daily reports
            const reportUrl = `/api/daily-reports?date=${reportDate}&job_id=${jobId}`;
            const reportResponse = await apiCall(reportUrl);
            if (reportResponse.success && reportResponse.data && reportResponse.data.length > 0) {
              const hasRealReport = reportResponse.data.some((report: any) => {
                const desc = (report.work_completed_description || '').trim().toLowerCase();
                return desc !== '' && !(desc.includes('photo') && desc.includes('upload') && desc.includes('only'));
              });
              if (hasRealReport) {
                console.log(`✅ Real daily report found on ${reportDate}`);
                foundRealReport = true;
                break;
              }
            }
          }

          if (!foundRealReport) {
            console.log('❌ Clock out blocked: No real report found on any date');
            setError(
              language === 'es'
                ? 'Por favor, ingrese una descripción de trabajo en su reporte diario antes de salir'
                : 'Please fill in a work description on your daily report before clocking out'
            );
            return;
          }
          } // end hours-floor else
        }
      } catch (err) {
        console.error('Error checking block photo-only clockout:', err);
        // Don't block clock out if the check fails (fail open)
      }
    }

    // Check if we need to auto-convert before clocking out
    // Handle both temporary shop clocks AND regular shop work at different job sites
    if (isTemporaryShopClock || (clockedInAtShop && selectedJob && SHOP_JOB_IDS.includes(selectedJob.job_id))) {
      // Find any job where user is currently within geofence
      // Allow conversion to different shop jobs OR field jobs (just not the same job)
      const jobAtLocation = allJobsWithDistance.find(job =>
        job.isWithinGeofence && selectedJob && job.job_id !== selectedJob.job_id
      );
      if (jobAtLocation) {
        const clockType = isTemporaryShopClock ? 'temporary shop clock' : 'shop work';
        console.log(`🔄 Auto-converting ${clockType} to:`, jobAtLocation.job_name);
        await handleConvertToJob(jobAtLocation);
      }
    }
    
    const clockInTime = new Date(currentEntry.clock_in);
    const isLongShift = qualifiesForLunch(clockInTime);
    const canChoose = canChooseLunch();
    
    // Determine lunch logic based on new rules
    let shouldPromptLunch = false;
    let autoLunchTaken = false;
    
    if (isLongShift) {
      if (canChoose) {
        // Special crew - prompt for choice
        shouldPromptLunch = true;
      } else {
        // Standard employee - automatic deduction
        autoLunchTaken = true;
      }
    }
    
    if (shouldPromptLunch && !showLunchConfirm) {
      setShowLunchConfirm(true);
      return;
    }
    
    // Determine final lunch settings
    const finalLunchTaken = shouldPromptLunch ? lunchTaken : autoLunchTaken;
    const finalLunchMinutes = finalLunchTaken ? 30 : 0;
    
    // Determine if clocking out at shop
    let clockingOutAtShop = false;
    if (currentShopLocation && selectedJob && !SHOP_JOB_IDS.includes(selectedJob.job_id)) {
      clockingOutAtShop = true;
    }
    
    setLoading(true);
    try {
      // Get pending geofence events from background service to sync with clock-out
      const pendingEvents = await backgroundGeolocationService.getPendingEvents();
      console.log(`📍 Found ${pendingEvents.length} pending geofence events to sync`);

      const response = await apiCall('/api/time/clock-out', 'POST', {
        lat: freshLocation.latitude,
        lng: freshLocation.longitude,
        lunch_taken: finalLunchTaken,
        lunch_minutes: finalLunchMinutes,
        clocked_out_at_shop: clockingOutAtShop,
        keep_job_billing: clockingOutAtShop,
        geofence_events: pendingEvents.map(e => ({
          jobId: e.jobId,
          eventType: e.eventType,
          eventTime: e.eventTime,
          lat: e.lat,
          lng: e.lng
        }))
      });
      
      if (response.success) {
        setCurrentStatus('not_clocked_in');
        setShowLunchConfirm(false);
        setLunchTaken(true);
        setClockedInAtShop(false);
        setIsTemporaryShopClock(false);
        setShopClockInTime(null);
        setCurrentEntry(null);
        setShowForgotButton(false);

        // Clear cached clock status
        if (user?.employee_id) {
          offlineService.clearClockStatus(user.employee_id);
        }

        // Clear synced geofence events and stop background tracking
        await backgroundGeolocationService.clearEventsForCurrentEntry();
        backgroundGeolocationService.stopTracking().catch(err => {
          console.error('📍 Error stopping background geolocation:', err);
        });

        showSuccess(language === 'es' ? 'Salida registrada exitosamente' : 'Clocked out successfully');

        // Run status checks in parallel for better performance
        await Promise.all([
          checkCurrentClockStatus(),
          loadWeeklyHours()
        ]);

        // Keep job selection after clock-out - user is still at the location
        // and should be able to clock back in. If they move to a different
        // location, they can select a new job from the dropdown.
      }
    } catch (err: any) {
      setError(err.message || (language === 'es' ? 'Error al registrar salida' : 'Clock out failed'));
    } finally {
      setLoading(false);
    }
  };

  // "Get My Location" in the retry modal: force a real GPS request, then
  // re-fire the clock action that was interrupted so it's one tap for the worker.
  const retryLocationAndRefire = async () => {
    if (!locationRetry) return;
    setLocationRetryStatus('trying');
    const fix = await forceRefreshLocation(true);
    if (!fix) {
      setLocationRetryStatus('failed');
      return;
    }
    const pending = locationRetry;
    setLocationRetry(null);
    setLocationRetryStatus('idle');
    lastActionTime.current = 0; // don't let the double-tap guard swallow the refire
    if (pending.action === 'in') {
      await handleClockIn(pending.laborCode);
    } else {
      await handleClockOut();
    }
  };

  // Simplified forgot clock out handler
  const handleForgotClockOut = async () => {
    if (preventDoubleTap()) return;

    if (!user?.employee_id || !currentEntry) return;

    setLoading(true);
    try {
      let clockOutTime: string;
      let needsReview = false;

      if (forgotClockOutMethod === 'location_data' && suggestedExitTime) {
        // Use the suggested EXIT time from geofence tracking
        console.log('Using location data time:', suggestedExitTime.time);
        clockOutTime = suggestedExitTime.time;
        // Location data doesn't need review - it's based on actual GPS data
        needsReview = false;
      } else if (forgotClockOutMethod === 'crew_average' && crewAverageTime) {
        // Convert crew average from 12-hour to 24-hour format
        console.log('Using crew average time:', crewAverageTime);
        const [time, period] = crewAverageTime.split(' ');
        const [hours, minutes] = time.split(':');
        let hour24 = parseInt(hours);

        // Convert to 24-hour
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }

        clockOutTime = `${hour24.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        console.log('Converted crew average to 24hr:', clockOutTime);
      } else {
        // Manual entry - HTML time input already provides 24-hour format
        clockOutTime = manualClockOutTime;
        needsReview = true;
        console.log('📱 PWA sending manual time (24hr):', clockOutTime);
        console.log('📱 manualClockOutTime state value:', manualClockOutTime);
        console.log('📱 forgotClockOutMethod:', forgotClockOutMethod);
      }

      // Simple validation
      if (!clockOutTime || !clockOutTime.match(/^\d{2}:\d{2}$/)) {
        setError('Invalid time format. Please try again.');
        return;
      }

      console.log('📤 PWA - Sending clock out time to API:', clockOutTime);
      console.log('📤 PWA - Current time:', new Date().toISOString());
      console.log('📤 PWA - Timezone offset:', new Date().getTimezoneOffset(), 'minutes');

      const clockInTime = new Date(currentEntry.clock_in);
      console.log('📤 PWA - Clock in time from DB:', currentEntry.clock_in);
      console.log('📤 PWA - Clock in as Date object:', clockInTime.toISOString());

      const isLongShift = qualifiesForLunch(clockInTime);
      const canChoose = canChooseLunch();

      // Apply same lunch logic as normal clock out
      let finalLunchTaken = false;
      if (isLongShift) {
        finalLunchTaken = canChoose ? lunchTaken : true; // Auto true for standard employees
      }

      // Get pending geofence events from background service to sync with clock-out
      const pendingEvents = await backgroundGeolocationService.getPendingEvents();
      console.log(`📍 Found ${pendingEvents.length} pending geofence events to sync`);

      const requestBody = {
        lat: location?.latitude || 0,
        lng: location?.longitude || 0,
        lunch_taken: finalLunchTaken,
        lunch_minutes: finalLunchTaken ? 30 : 0,
        custom_clock_out_time: clockOutTime,
        timezone_offset_minutes: new Date().getTimezoneOffset(), // Send user's timezone offset
        needs_review: needsReview,
        geofence_events: pendingEvents.map(e => ({
          jobId: e.jobId,
          eventType: e.eventType,
          eventTime: e.eventTime,
          lat: e.lat,
          lng: e.lng
        }))
      };

      console.log('📤 PWA - Full API request body:', requestBody);
      console.log('📤 PWA - User timezone offset:', new Date().getTimezoneOffset(), 'minutes');

      // Use existing clock-out API with custom time
      const response = await apiCall('/api/time/clock-out', 'POST', requestBody);
      
      if (response.success) {
        setCurrentStatus('not_clocked_in');
        setShowForgotClockOut(false);
        setClockedInAtShop(false);
        setIsTemporaryShopClock(false);
        setShopClockInTime(null);
        setCurrentEntry(null);
        setShowForgotButton(false);

        // Clear cached clock status
        if (user?.employee_id) {
          offlineService.clearClockStatus(user.employee_id);
        }

        // Clear synced geofence events and stop background tracking
        await backgroundGeolocationService.clearEventsForCurrentEntry();
        backgroundGeolocationService.stopTracking().catch(err => {
          console.error('📍 Error stopping background geolocation:', err);
        });

        // Reset suggested exit time
        setSuggestedExitTime(null);

        const successMsg = needsReview ?
          (language === 'es' ? 'Salida registrada (requiere revisión)' : 'Clocked out (flagged for review)') :
          (language === 'es' ? 'Salida registrada exitosamente' : 'Clocked out successfully');
        showSuccess(successMsg);

        // Run status checks in parallel for better performance
        await Promise.all([
          checkCurrentClockStatus(),
          loadWeeklyHours()
        ]);

        // Keep job selection after clock-out - user can select a different job if needed
      }
    } catch (err: any) {
      console.error('Forgot clock out error:', err);
      setError(err.message || (language === 'es' ? 'Error al registrar salida' : 'Clock out failed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle background location disclosure acceptance (Google Play requirement)
  const handleBackgroundLocationAllow = () => {
    console.log('📍 User accepted background location disclosure');
    localStorage.setItem(BACKGROUND_LOCATION_ACCEPTED_KEY, 'true');
    setShowBackgroundLocationDisclosure(false);

    // Now start tracking with the pending job ID
    if (pendingBackgroundTrackingJobId) {
      backgroundGeolocationService.startTracking(pendingBackgroundTrackingJobId).then(started => {
        if (started) {
          console.log('📍 Background geolocation tracking started after disclosure');
        } else {
          console.log('📍 Background geolocation permission denied by OS');
        }
      }).catch(err => {
        console.error('📍 Error starting background geolocation:', err);
      });
      setPendingBackgroundTrackingJobId(null);
    }
  };

  // Handle background location disclosure decline
  const handleBackgroundLocationDecline = () => {
    console.log('📍 User declined background location disclosure');
    setShowBackgroundLocationDisclosure(false);
    setPendingBackgroundTrackingJobId(null);
    // Don't save to localStorage - will ask again next clock-in
  };

  const handleConvertToJob = async (job: JobWithDistance) => {
    if (preventDoubleTap()) return;

    // Allow converting from any job to any other job (not just shop-to-job)
    if (!currentEntry) return;

    setLoading(true);
    try {
      // Get timezone offset in minutes (e.g., EST = 240, PST = 420)
      const timezoneOffsetMinutes = new Date().getTimezoneOffset();

      // Determine billing method based on whether this is a temporary shop clock:
      // - isTemporaryShopClock = true: User clocked in at shop with field work code (CONC, GRADE, etc.)
      //   They're just starting at shop, all time bills to the field job → use 'bill_all' (1 entry)
      // - isTemporaryShopClock = false: User is doing actual shop work OR switching between field jobs
      //   Different work types → use 'split' (2 entries)
      const billingMethod = isTemporaryShopClock ? 'bill_all' : 'split';

      console.log('🔄 Job switch:', {
        from: selectedJob?.job_name,
        to: job.job_name,
        isTemporaryShopClock,
        billingMethod
      });

      // Backend will auto-set labor_code to 'SHOP' if switching to shop job
      const response = await apiCall('/api/time/switch-job', 'POST', {
        job_id: job.job_id,
        lat: location?.latitude || null,
        lng: location?.longitude || null,
        conversion_reason: clockedInAtShop ? 'shop_to_job' : 'job_to_job',
        timezone_offset_minutes: timezoneOffsetMinutes,
        billing_method: billingMethod,
        switch_source: 'manual'
      });

      if (response.success) {
        // Update state based on the new job
        const isNewJobShop = SHOP_JOB_IDS.includes(job.job_id);
        setClockedInAtShop(isNewJobShop);
        setIsTemporaryShopClock(false);
        setSelectedJob(job);
        showSuccess(language === 'es' ? `Convertido a ${job.job_name}` : `Converted to ${job.job_name}`);
        setShowConvertPrompt(false);
        setTargetJob(null);

        // Update background geolocation service with new job
        backgroundGeolocationService.updateCurrentJob(job.job_id);
        backgroundGeolocationService.markJobAsHandled(job.job_id);

        await checkCurrentClockStatus();

        // Navigate to dashboard after successful job switch
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || (language === 'es' ? 'Error al convertir' : 'Failed to switch job'));
    } finally {
      setLoading(false);
    }
  };

  // User chose which of the overlapping sites they're working at.
  const handleMultiSitePick = (job: JobWithDistance) => {
    setSelectedJob(job);
    localStorage.setItem('current_job_id', job.job_id.toString());
    setShowMultiSitePrompt(false);
  };

  const handleJobSelect = (newJob: JobWithDistance) => {
    // Allow job changes if user is at the target job's geofence
    if (currentStatus === 'clocked_in' && clockedInAtShop) {
      if (!newJob.isWithinGeofence) {
        setError(t.mustBeAtTargetJob);
        setShowJobSelector(false);
        return;
      }

      // If temporary shop clock and at job site, convert
      if (isTemporaryShopClock) {
        handleConvertToJob(newJob);
        setShowJobSelector(false);
        return;
      }

      // For regular shop work, allow job change if at the target job location
      if (!isTemporaryShopClock && newJob.isWithinGeofence) {
        // Convert shop work to regular job work
        handleConvertToJob(newJob);
        setShowJobSelector(false);
        return;
      }
    }

    setSelectedJob(newJob);
    localStorage.setItem('current_job_id', newJob.job_id.toString());
    setShowJobSelector(false);
  };

  const handleLunchConfirm = (taken: boolean) => {
    setLunchTaken(taken);
    setShowLunchConfirm(false);
    handleClockOut();
  };

  const handleDayClick = async (day: WeekDay) => {
    if (!user?.employee_id || day.hours === 0) return;

    setSelectedDay(day);
    setShowDayDetails(true);
    setLoading(true);

    try {
      // Pass the plain YYYY-MM-DD Eastern date for BOTH bounds. GET /api/time
      // filters on the entry's Eastern clock-in DATE, so a local-midnight ISO end
      // bound (…T23:59:59 local → next UTC day) pulled the FOLLOWING day's entries
      // into this day's detail — showing that day's lunch as a 2nd lunch. day.date
      // is already the EST date used to build the weekly list.
      const response = await apiCall(
        `/api/time?employee_id=${user.employee_id}&start_date=${day.date}&end_date=${day.date}&_t=${Date.now()}`
      );

      if (response.success && response.data) {
        setDayEntries(response.data);
      }
    } catch (err) {
      console.error('Failed to load day entries:', err);
      setError('Failed to load day details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseYesterdayEntry = async () => {
    if (preventDoubleTap()) return;

    if (!openEntry || !user?.employee_id) return;

    setLoading(true);
    try {
      let clockOutTime: string;
      let needsReview = false;

      if (forgotClockOutMethod === 'location_data' && suggestedExitTime) {
        // Use the suggested EXIT time from geofence tracking
        console.log('Using location data time for old entry:', suggestedExitTime.time);
        clockOutTime = suggestedExitTime.time;
        // Location data doesn't need review - it's based on actual GPS data
        needsReview = false;
      } else if (forgotClockOutMethod === 'crew_average' && crewAverageTime) {
        // Convert crew average from 12-hour to 24-hour format
        console.log('Using crew average time for old entry:', crewAverageTime);
        const [time, period] = crewAverageTime.split(' ');
        const [hours, minutes] = time.split(':');
        let hour24 = parseInt(hours);

        // Convert to 24-hour
        if (period === 'PM' && hour24 !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hour24 === 12) {
          hour24 = 0;
        }

        clockOutTime = `${hour24.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        console.log('Converted crew average to 24hr for old entry:', clockOutTime);
      } else {
        // Manual entry - HTML time input already provides 24-hour format
        clockOutTime = manualClockOutTime;
        needsReview = true;
        console.log('📱 PWA sending manual time for old entry (24hr):', clockOutTime);
        console.log('📱 manualClockOutTime state value for old entry:', manualClockOutTime);
        console.log('📱 forgotClockOutMethod for old entry:', forgotClockOutMethod);
      }

      // Simple validation
      if (!clockOutTime || !clockOutTime.match(/^\d{2}:\d{2}$/)) {
        setError('Invalid time format. Please try again.');
        return;
      }

      console.log('Final clock out time for old entry:', clockOutTime);

      // Get timezone offset in minutes (negative for UTC-X, positive for UTC+X)
      const timezoneOffsetMinutes = new Date().getTimezoneOffset();
      console.log('📱 Timezone offset (minutes):', timezoneOffsetMinutes);

      // Get pending geofence events from background service to sync with clock-out
      const pendingEvents = await backgroundGeolocationService.getPendingEvents();
      console.log(`📍 Found ${pendingEvents.length} pending geofence events to sync`);

      // Since force-clock-out doesn't exist, use regular clock-out with custom time
      const response = await apiCall('/api/time/clock-out', 'POST', {
        lat: location?.latitude || 0,
        lng: location?.longitude || 0,
        lunch_taken: true,
        lunch_minutes: 30,
        custom_clock_out_time: clockOutTime,
        timezone_offset_minutes: timezoneOffsetMinutes,
        needs_review: needsReview,
        geofence_events: pendingEvents.map(e => ({
          jobId: e.jobId,
          eventType: e.eventType,
          eventTime: e.eventTime,
          lat: e.lat,
          lng: e.lng
        }))
      });

      if (response.success) {
        const successMsg = needsReview ?
          (language === 'es' ? 'Entrada anterior cerrada (requiere revisión)' : 'Previous entry closed (flagged for review)') :
          (language === 'es' ? 'Entrada anterior cerrada' : 'Previous entry closed');
        showSuccess(successMsg);
        setShowOpenEntryModal(false);
        setOpenEntry(null);

        // Clear synced geofence events and stop background tracking
        await backgroundGeolocationService.clearEventsForCurrentEntry();
        backgroundGeolocationService.stopTracking().catch(err => {
          console.error('📍 Error stopping background geolocation:', err);
        });

        // Reset suggested exit time
        setSuggestedExitTime(null);

        // Run status checks in parallel for better performance
        await Promise.all([
          checkCurrentClockStatus(),
          loadWeeklyHours()
        ]);
      }
    } catch (err: any) {
      console.error('Close old entry error:', err);
      setError(err.message || (language === 'es' ? 'Error al cerrar entrada' : 'Failed to close entry'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    injectAnimationStyles();
    const storedUser = localStorage.getItem('bfa_user');
    const storedToken = localStorage.getItem('bfa_token');
    const storedLang = localStorage.getItem('language') || 'en';
    
    // Check if we have auth credentials
    if (!storedToken || !storedUser) {
      console.error('No authentication credentials found');
      window.location.href = '/login';
      return;
    }
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        userRef.current = parsedUser; // Keep ref in sync for event handlers
        setLoadingStates(prev => ({ ...prev, user: false }));
      } catch (e) {
        console.error('Invalid user data in storage');
        localStorage.removeItem('bfa_user');
        window.location.href = '/login';
        return;
      }
    }

    setLanguage(storedLang as 'en' | 'es');

    // DON'T request location on mount - wait for user action (OK button or job selector)
    // This prevents iPad/iOS from showing location permission prompt every page load
    // Location will be requested when user clicks "Select Your Location" prompt or opens job selector
    loadJobs();

    // Exception: admins. Field roles arrive here with a fresh fix because their
    // dashboard tiles and the bottom-nav Time tab fetch one before navigating.
    // Admin dashboard tiles deliberately don't (some admins don't want their
    // location read outside this page), so ask for one now, on this page only.
    // Non-blocking; a failure shows in the usual location box with its reason.
    try {
      const role = storedUser ? JSON.parse(storedUser)?.role : null;
      if (role === 'admin') requestLocation().catch(() => {});
    } catch { /* ignore */ }
    loadUnknownJobSetting();
    loadTimecardApprovalSettings();
    
    // Set up forgot button check
    lateShiftCheckInterval.current = setInterval(checkForgotButtonVisibility, 60000); // Check every minute
    
    // Listen for language changes
    const handleLanguageChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newLang = (customEvent.detail || 'en') as 'en' | 'es';
      setLanguage(newLang);
      console.log('Language changed to:', newLang);
    };

    window.addEventListener('languageChange', handleLanguageChange);

    // Network status monitoring - handle real-time offline/online transitions
    const handleOnline = async () => {
      console.log('🌐 Network connection restored - re-checking status');
      setIsOffline(false);
      setUsingCachedStatus(false);
      // Re-load jobs and check status when connection restored (use ref to avoid closure issues)
      if (userRef.current?.employee_id) {
        // Reload jobs first (may have failed during offline init), then check status
        await loadJobs();
        await checkCurrentClockStatus();
      }
    };

    const handleOffline = () => {
      console.log('📵 Network connection lost - switching to cached status');
      // Immediately switch to offline mode and try to use cached status
      if (user?.employee_id) {
        const cached = offlineService.getCachedClockStatus(user.employee_id);
        if (cached) {
          console.log('📱 Using cached clock status (network lost)', cached);
          setIsOffline(true);
          setUsingCachedStatus(true);
          setCurrentStatus(cached.currentStatus);
          const entry = (cached.currentEntry as unknown) as TimeEntry | null;
          setCurrentEntry(entry);
          setClockedInAtShop(cached.clockedInAtShop);
          setIsTemporaryShopClock(cached.isTemporaryShopClock);
          if (cached.shopClockInTime) {
            setShopClockInTime(new Date(cached.shopClockInTime));
          }
          // Restore selected job
          if (entry && entry.job_id && jobs.length > 0) {
            const clockedJob = jobs.find(j => j.job_id === entry.job_id);
            if (clockedJob) {
              const jobWithDistance: JobWithDistance = {
                ...clockedJob,
                distance: 'Cached',
                distanceValue: 0,
                distanceInMiles: 0,
                isWithinGeofence: false
              };
              setSelectedJob(jobWithDistance);
            }
          }
        } else {
          console.log('⚠️ Network lost but no cached status available');
          setIsOffline(true);
          setUsingCachedStatus(false);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Visibility change handler - redirect to dashboard when app loses focus
    // This ensures fresh location is fetched when they click Time Clock again from dashboard
    // Prevents stale location data when user moves to a new job site while app was in background
    // BUT: Don't redirect when camera is open (Unknown job photo capture)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Don't redirect if camera is open for Unknown job photo (use ref to avoid stale closure)
        if (isCapturingPhotoRef.current) {
          console.log('📱 App lost focus but camera is open - skipping redirect');
          return;
        }
        console.log('📱 App lost focus - redirecting to dashboard for fresh location on return');
        // Redirect to dashboard so when they come back, they're on dashboard
        // and clicking Time Clock will refresh location
        navigate('/');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // pageshow event for iOS Safari (bfcache restoration) - redirect to dashboard
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('📱 Page restored from bfcache - redirecting to dashboard for fresh location');
        navigate('/');
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      if (successTimeout.current) {
        clearTimeout(successTimeout.current);
      }
      if (lateShiftCheckInterval.current) {
        clearInterval(lateShiftCheckInterval.current);
      }
    };
  }, []); // Run once on mount - don't depend on user/jobs to prevent infinite loop

  useEffect(() => {
    if (user?.employee_id && jobs.length > 0) {
      checkCurrentClockStatus();
      loadWeeklyHours();
    }
  }, [user, jobs]);

  useEffect(() => {
    calculateDistances();
  }, [calculateDistances]);

  // Re-run auto-selection when status changes from loading
  useEffect(() => {
    if (currentStatus !== 'loading' && location && jobs.length > 0) {
      calculateDistances();
    }
  }, [currentStatus]);

  // iOS Safari fix: Check localStorage directly for cached location when jobs load
  // This handles React state/localStorage sync issues on iOS where the location context
  // may not have synced the cached location to state yet, OR when calculateDistances
  // didn't auto-select a job for some reason
  useEffect(() => {
    // Run if jobs loaded, not loading status, and no job selected yet
    // Don't require !location - we want this as a fallback even if context has location
    if (jobs.length > 0 && currentStatus !== 'loading' && !selectedJob) {
      try {
        const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (cached) {
          const parsedLocation = JSON.parse(cached);
          const age = Date.now() - parsedLocation.timestamp;

          if (age < LOCATION_CACHE_DURATION) {
            console.log('[ClockInOut] iOS fix: Using cached location from localStorage for job auto-select (age:', Math.round(age/1000), 'sec)');

            // Calculate distances and find job user is at
            const jobsWithDist = jobs.map(job => {
              const distanceInMeters = calculateDistance(
                parsedLocation.latitude,
                parsedLocation.longitude,
                job.lat,
                job.lng
              );
              const distanceInMiles = metersToMiles(distanceInMeters);
              const isWithinGeofence = isUserWithinJobGeofence(job, parsedLocation.latitude, parsedLocation.longitude);

              return {
                ...job,
                distance: `${distanceInMiles.toFixed(2)} ${t.milesAway}`,
                distanceValue: distanceInMeters,
                distanceInMiles,
                isWithinGeofence
              };
            }).sort((a, b) => (a.distanceValue || 0) - (b.distanceValue || 0));

            // Find job user is at (within geofence)
            const jobUserIsAt = jobsWithDist.find(job => job.isWithinGeofence);

            // Update nearby jobs and all jobs with distance data
            const nearby = jobsWithDist.filter(job => (job.distanceInMiles || 0) <= NEARBY_RADIUS_MILES || job.isWithinGeofence);
            setNearbyJobs(nearby);
            setAllJobsWithDistance(jobsWithDist);

            if (jobUserIsAt) {
              console.log('[ClockInOut] iOS fix: Auto-selecting job:', jobUserIsAt.job_name);
              setSelectedJob(jobUserIsAt);
              localStorage.setItem('current_job_id', jobUserIsAt.job_id.toString());
              setUserAtJobSite(true);
            } else {
              console.log('[ClockInOut] iOS fix: No job within geofence, showing', nearby.length, 'nearby jobs');
              setUserAtJobSite(false);
            }
          } else {
            console.log('[ClockInOut] Cached location expired (age:', Math.round(age/1000/60), 'min)');
          }
        } else {
          console.log('[ClockInOut] No cached location in localStorage');
        }
      } catch (e) {
        console.error('[ClockInOut] Error reading cached location from localStorage:', e);
      }
    }
  }, [jobs, currentStatus, selectedJob, t.milesAway, isUserWithinJobGeofence]);

  // When location becomes available AND user is clocked in, recalculate clock status
  // This ensures geofence calculations happen with real location data (fixes Switch Job button)
  // Uses locationLoading instead of allJobsWithDistance to avoid infinite loop
  useEffect(() => {
    if (!locationLoading && location && jobs.length > 0 && user?.employee_id) {
      // Only run once when location finishes loading
      checkCurrentClockStatus();
    }
  }, [locationLoading, location, jobs.length, user?.employee_id]);

  useEffect(() => {
    checkForgotButtonVisibility();
  }, [currentStatus, currentEntry]);

  // Load crew average when forgot modal opens
  useEffect(() => {
    if (showForgotClockOut || showOpenEntryModal) {
      loadCrewAverage();
    }
  }, [showForgotClockOut, showOpenEntryModal]);

  // Re-load weekly hours when language changes to update day names
  useEffect(() => {
    if (user?.employee_id) {
      loadWeeklyHours();
    }
  }, [language]);

  // Load vacation schedule
  useEffect(() => {
    const loadVacationSchedule = async () => {
      if (!user?.employee_id) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/vacation/public/schedule/${user.employee_id}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();
        if (data.success && data.data?.length > 0) {
          // Find vacation that overlaps with current or upcoming week
          const now = new Date();
          const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          const upcomingVacation = data.data.find((v: any) => {
            // Add T12:00:00 to avoid timezone shift when parsing date-only strings
            const startStr = v.start_date.includes('T') ? v.start_date : v.start_date + 'T12:00:00';
            const endStr = v.end_date.includes('T') ? v.end_date : v.end_date + 'T12:00:00';
            const startDate = new Date(startStr);
            const endDate = new Date(endStr);
            return (startDate <= weekFromNow && endDate >= now);
          });
          if (upcomingVacation) {
            setVacationSchedule({
              start_date: upcomingVacation.start_date,
              end_date: upcomingVacation.end_date,
              hours_requested: upcomingVacation.hours_requested || 40
            });
          }
        }
      } catch (err) {
        console.log('Vacation schedule not available');
      }
    };
    loadVacationSchedule();
  }, [user?.employee_id]);

  // Render helpers
  const renderClockButton = () => {
    // Look up fresh geofence status from allJobsWithDistance to avoid stale data
    const selectedJobWithGeofence = selectedJob
      ? allJobsWithDistance.find(j => j.job_id === selectedJob.job_id) || selectedJob
      : null;
    // Allow clock-in if:
    // 1. Within geofence of selected job (or within the small grace buffer when
    //    it's the sole nearby job — the fresh-fix recheck enforces this again), OR
    // 2. At a shop location, OR
    // 3. Unknown job clock-in is enabled (even without job selected - will use Unknown job)
    const clockInBufferOk = !!(selectedJobWithGeofence && location &&
      isSoleNearbyJob(selectedJobWithGeofence.job_id) &&
      isNearJobWithBuffer(selectedJobWithGeofence, location.latitude, location.longitude, CLOCK_IN_GEOFENCE_BUFFER_M));
    const canClockIn = (selectedJobWithGeofence && (selectedJobWithGeofence.isWithinGeofence || clockInBufferOk || currentShopLocation)) || allowUnknownJobClockin;

    // Debug logging for clock-in button state
    if (selectedJob && !canClockIn) {
      console.log('🔴 Clock-in disabled:', {
        selectedJobName: selectedJob.job_name,
        hasLocation: !!location,
        locationAccuracy,
        isWithinGeofence: selectedJobWithGeofence?.isWithinGeofence,
        currentShopLocation,
        canClockIn
      });
    }

    // Show loading state until status is determined - not rendered, using full screen overlay instead
    if (currentStatus === 'loading') {
      return null;
    }

    if (currentStatus === 'not_clocked_in') {
      // Don't check GPS accuracy if user is already within geofence (canClockIn = true)
      // The geofence check already validates they're at the correct location
      const buttonDisabled = loading || !canClockIn || usingCachedStatus;

      // Debug why button is disabled
      if (buttonDisabled) {
        console.log('🔴 Clock-in button DISABLED:', {
          loading,
          canClockIn,
          usingCachedStatus,
          allowUnknownJobClockin,
          selectedJob: selectedJob?.job_name,
          isWithinGeofence: selectedJobWithGeofence?.isWithinGeofence,
          currentShopLocation: !!currentShopLocation
        });
      }

      return (
        <button
          style={{
            ...styles.clockButton,
            ...styles.clockInButton,
            opacity: buttonDisabled ? 0.5 : 1
          }}
          onClick={handleClockInButtonClick}
          disabled={buttonDisabled}
        >
          <Clock size={32} />
          <span style={styles.buttonText}>{t.clockIn}</span>
          <span style={styles.buttonSubtext}>
            {currentShopLocation ? t.atShop : t.startDay}
          </span>
        </button>
      );
    }
    
    return (
      <>
{(() => {
          // Find any job where user is within geofence (for conversion)
          // Allow conversion to different shop jobs OR field jobs (just not the same job)
          const jobAtLocation = allJobsWithDistance.find(job =>
            job.isWithinGeofence && selectedJob && job.job_id !== selectedJob.job_id
          );

          // Show NEW JOB button if:
          // 1. User has temporary shop clock, OR
          // 2. User is clocked into any shop work but physically at a different job site, OR
          // 3. User is clocked into a regular job but physically at a different job site
          const shouldShowNewJobButton = jobAtLocation && (clockedInAtShop || selectedJob);

          return shouldShowNewJobButton && (
            <button
              style={{
                ...styles.clockButton,
                ...styles.convertButton,
                opacity: (loading || usingCachedStatus) ? 0.7 : 1
              }}
              onClick={() => handleConvertToJob(jobAtLocation)}
              disabled={loading || usingCachedStatus}
            >
              {loading ? (
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <RefreshCw size={24} />
              )}
              <span style={styles.buttonText}>
                {loading ? (language === 'es' ? 'Cambiando...' : 'Switching...') : t.convertToJob}
              </span>
              {!loading && <span style={styles.buttonSubtext}>to {jobAtLocation.job_name}</span>}
            </button>
          );
        })()}

        <button
          style={{
            ...styles.clockButton,
            ...styles.clockOutButton,
            opacity: (loading || usingCachedStatus) ? 0.7 : 1
          }}
          onClick={handleClockOut}
          disabled={loading || usingCachedStatus}
        >
          {loading ? (
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Clock size={32} />
          )}
          <span style={styles.buttonText}>
            {loading ? (language === 'es' ? 'Registrando...' : 'Clocking Out...') : t.clockOut}
          </span>
          {!loading && <span style={styles.buttonSubtext}>{t.endDay}</span>}
        </button>
        
        {/* New Forgot Clock Out Button */}
        {showForgotButton && (
          <div style={{ position: 'relative', marginTop: '8px' }}>
            <button
              style={{
                ...styles.clockButton,
                backgroundColor: '#f59e0b',
                color: 'white',
                fontSize: '14px',
                padding: '12px',
                width: '100%'
              }}
              onClick={() => setShowForgotClockOut(true)}
              disabled={loading}
            >
              <AlertCircle size={20} />
              <span style={styles.buttonText}>{t.iForgotToClockOut}</span>
            </button>
            {/* Dismiss button (X) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissForgotButton();
              }}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#374151',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
              title={language === 'es' ? 'No mostrar hoy' : "Don't show today"}
            >
              ×
            </button>
          </div>
        )}
      </>
    );
  };

  // Get the current job with proper typing
  // For any shop work, show where user actually is instead of shop if they're at a job site
  const currentJobTyped = (() => {
    // If clocked into shop work but at a different job site, show the actual job site
    if (clockedInAtShop) {
      const jobAtLocation = allJobsWithDistance.find(job =>
        job.isWithinGeofence && !SHOP_JOB_IDS.includes(job.job_id)
      );
      if (jobAtLocation) {
        return jobAtLocation;
      }
    }

    return selectedJob
      ? allJobsWithDistance.find(j => j.job_id === selectedJob.job_id) || selectedJob
      : null;
  })();

  // Check if app is fully loaded
  const isFullyLoaded = !loadingStates.user && !loadingStates.jobs && !loadingStates.clockStatus;
  const isInitializing = loadingStates.user || loadingStates.jobs || loadingStates.clockStatus;

  // Retry function for offline status check
  const retryClockStatusCheck = async () => {
    setLoadingStates(prev => ({ ...prev, clockStatus: true }));
    await checkCurrentClockStatus();
  };

  // Check if user has "Always" location permission (native apps only)
  const checkLocationPermission = async (): Promise<boolean> => {
    // Only check on native platforms
    if (!Capacitor.isNativePlatform()) {
      return true;
    }

    try {
      const status = await BackgroundGeolocation.getProviderState();
      console.log('[ClockIn] Location permission status:', status.status);

      // AUTHORIZATION_STATUS_ALWAYS = 3
      return status.status === BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS;
    } catch (error) {
      console.error('[ClockIn] Error checking location permission:', error);
      // If we can't check, allow them through - the actual tracking will handle errors
      return true;
    }
  };

  // Open device settings for location permission
  const openLocationSettings = async () => {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        // iOS: Open app settings
        window.open('app-settings:', '_system');
      } else {
        // Android: Request permission again (will show system dialog or settings)
        await BackgroundGeolocation.requestPermission();
      }
    } catch (error) {
      console.error('[ClockIn] Error opening settings:', error);
    }
    setShowPermissionRequired(false);
  };

  // Handler for Clock In button - checks for Friday timecard review first
  const handleClockInButtonClick = async () => {
    console.log('📋 Clock In button clicked, checking permissions...');
    setCheckingClockIn(true);   // instant feedback — the checks below can take a few seconds
    try {
      // Check location permission first (native apps only)
      const hasAlwaysPermission = await checkLocationPermission();
      if (!hasAlwaysPermission) {
        console.log('📋 Location permission not "Always", showing modal');
        setShowPermissionRequired(true);
        return;
      }

      console.log('📋 Location permission OK, checking timecard review...');
      // Check if we need to show Friday timecard review modal
      const shouldShowReview = await checkFridayTimecardReview();
      console.log('📋 shouldShowReview:', shouldShowReview);

      if (!shouldShowReview) {
        // No review needed, proceed to labor code selector
        console.log('📋 No review needed, showing labor code selector');
        setShowLaborCodeSelector(true);
      }
      // If review is needed, the modal will be shown and labor code selector
      // will be shown after the review is completed/skipped
    } finally {
      setCheckingClockIn(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Instant feedback while the Clock-In pre-checks run (so the tap registers) */}
      {checkingClockIn && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <Loader2 size={44} color="#ffffff" style={{ animation: 'spin 1s linear infinite' }} />
          <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 600 }}>
            {language === 'es' ? 'Un momento…' : 'One moment…'}
          </div>
        </div>
      )}

      {/* Loading Overlay - shown during initial app load or when offline with no cache */}
      {isInitializing && (
        <div style={styles.loadingOverlay}>
          {isOffline && !usingCachedStatus && !loadingStates.user && !loadingStates.jobs ? (
            // No Service Screen (when offline and no cached status)
            <>
              <WifiOff size={64} color="#ef4444" style={{ marginBottom: '20px' }} />
              <div style={{ ...styles.loadingText, fontSize: '20px', fontWeight: 'bold', marginBottom: '10px' }}>
                {language === 'es' ? 'Sin Conexión' : 'No Connection'}
              </div>
              <div style={{ ...styles.loadingText, fontSize: '16px', opacity: 0.9, marginBottom: '30px', maxWidth: '300px', textAlign: 'center' }}>
                {language === 'es'
                  ? 'No se puede verificar el estado del reloj. Verifique su conexión e intente nuevamente.'
                  : 'Cannot verify clock status. Check your connection and try again.'}
              </div>
              <button
                onClick={retryClockStatusCheck}
                style={{
                  ...styles.clockButton,
                  backgroundColor: '#3b82f6',
                  width: 'auto',
                  padding: '12px 32px',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw size={20} />
                {language === 'es' ? 'Reintentar' : 'Retry'}
              </button>
            </>
          ) : (
            // Normal loading state
            <>
              <Loader2 size={48} style={styles.loadingSpinner} color="white" />
              <div style={styles.loadingText}>{t.initializing}</div>
              {locationError && (
                <div style={{...styles.locationErrorInOverlay}}>
                  <AlertCircle size={20} />
                  <span style={{ marginLeft: '8px', fontSize: '14px' }}>{locationError}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={styles.successMessage}>
          <Check size={28} strokeWidth={3} />
          {successMessage}
        </div>
      )}

      {/* Vacation Banner - Show if vacation scheduled for current/upcoming week */}
      {vacationSchedule && isFullyLoaded && (
        <VacationBanner
          startDate={vacationSchedule.start_date}
          endDate={vacationSchedule.end_date}
        />
      )}

      {/* Offline Warning Banner - Showing Cached Status */}
      {usingCachedStatus && isFullyLoaded && (
        <div style={{
          ...styles.criticalErrorBanner,
          backgroundColor: '#f59e0b',
          borderColor: '#f59e0b'
        }}>
          <div style={styles.errorBannerContent}>
            <WifiOff size={24} color="#ffffff" />
            <div style={styles.errorBannerText}>
              <strong style={styles.errorBannerTitle}>
                {language === 'es' ? 'Modo Sin Conexión' : 'Offline Mode'}
              </strong>
              <p style={styles.errorBannerMessage}>
                {language === 'es'
                  ? 'Mostrando el último estado conocido. Acciones deshabilitadas hasta que se restablezca la conexión.'
                  : 'Showing last known status. Actions disabled until connection restored.'}
              </p>
            </div>
          </div>
          <button
            style={styles.retryButton}
            onClick={retryClockStatusCheck}
          >
            <RefreshCw size={16} />
            {language === 'es' ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {/* Critical Error Banner - Location Services */}
      {locationError && isFullyLoaded && (
        <div style={styles.criticalErrorBanner}>
          <div style={styles.errorBannerContent}>
            <AlertCircle size={24} color="#ef4444" />
            <div style={styles.errorBannerText}>
              <strong style={styles.errorBannerTitle}>
                {language === 'es' ? 'Error de Ubicación' : 'Location Error'}
              </strong>
              <p style={styles.errorBannerMessage}>{locationError}</p>
            </div>
          </div>
          <button
            style={styles.retryButton}
            onClick={() => requestLocation()}
          >
            <RefreshCw size={16} />
            <span style={{ marginLeft: '6px' }}>{t.retry}</span>
          </button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>{t.timeClockTitle}</h1>
          {user && (
            <div style={styles.userName}>
              {user.first_name} {user.last_name}
            </div>
          )}
        </div>
        <div style={styles.statusBadge}>
          <div style={{
            ...styles.statusDot,
            backgroundColor: currentStatus === 'clocked_in' ? '#10b981' : currentStatus === 'loading' ? '#6b7280' : '#ef4444'
          }} />
          {currentStatus === 'clocked_in' ? t.clockedIn : currentStatus === 'loading' ? t.loading : t.notClockedIn}
        </div>
      </div>

      {/* Warnings */}
      {location && !userAtJobSite && !currentShopLocation && (
        <div style={styles.warningBar}>
          <AlertCircle size={20} />
          <span>{t.notAtJobSite}</span>
        </div>
      )}

      {/* Admin-only diagnostic: the exact fix the page is working from. Lets an
          admin see at a glance whether "not at any job site" is a geofence
          question or a bad/stale/coarse position (2026-10-30). */}
      {user?.role === 'admin' && location && (
        <div style={{ textAlign: 'center', fontSize: '12px', color: '#64748b', marginTop: '-4px', marginBottom: '8px' }}>
          📍 {location.latitude.toFixed(5)}, {location.longitude.toFixed(5)}
          {location.accuracy != null ? ` ±${Math.round(location.accuracy)} m` : ''}
          {' · '}{Math.max(0, Math.round((Date.now() - location.timestamp) / 1000))}s ago
          {' · '}
          <a href={`https://maps.google.com/?q=${location.latitude},${location.longitude}`} target="_blank" rel="noreferrer" style={{ color: '#6366f1' }}>map</a>
        </div>
      )}

      {locationAccuracy && locationAccuracy > 100 && (
        <div style={styles.accuracyWarning}>
          <AlertCircle size={16} />
          <span>{t.lowAccuracy}: {Math.round(locationAccuracy)}m</span>
        </div>
      )}

      {isTemporaryShopClock && (
        <div style={styles.shopStatusBar}>
          <Clock size={20} />
          <span>{t.temporaryShopClock}</span>
        </div>
      )}

      {/* Show when clocked into shop but at different job site */}
      {clockedInAtShop && !isTemporaryShopClock && currentJobTyped && !SHOP_JOB_IDS.includes(currentJobTyped.job_id) && (
        <div style={styles.shopStatusBar}>
          <Clock size={20} />
          <span>{language === 'es' ? 'Trabajo de taller en sitio' : 'Shop work at job site'}</span>
        </div>
      )}

      {/* Job Selector */}
      <div style={styles.jobSelector} onClick={async () => {
        setShowJobSelector(true);
        // Force fresh location when opening job selector to ensure accurate geofence data
        console.log('📍 Job selector opened - refreshing location...');
        await forceRefreshLocation();
      }}>
        <div style={styles.jobSelectorContent}>
          <div style={styles.jobSelectorLabel}>{t.currentJob}</div>
          {currentJobTyped ? (
            <>
              <div style={styles.jobName}>{currentJobTyped.job_name}</div>
              <div style={styles.jobAddress}>
                <span style={styles.locationIcon}>📍</span>
                <span>
                  {SHOP_JOB_IDS.includes(currentJobTyped.job_id) && currentShopLocation
                    ? `${currentShopLocation.name} - Shop Location`
                    : `${currentJobTyped.address || currentJobTyped.bradanna_job_number}, ${currentJobTyped.city}`
                  }
                </span>
                {currentJobTyped.distance && !SHOP_JOB_IDS.includes(currentJobTyped.job_id) && (
                  <span style={styles.jobDistance}> • {currentJobTyped.distance}</span>
                )}
              </div>
              {currentJobTyped.isWithinGeofence && (
                <div style={styles.atLocationBadge}>
                  📍 {t.atLocation}
                </div>
              )}
              {currentShopLocation && SHOP_JOB_IDS.includes(currentJobTyped.job_id) && (
                <div style={styles.atLocationBadge}>
                  🏭 {t.atShop}
                </div>
              )}
            </>
          ) : (
            <div style={styles.jobPlaceholder}>
              {!userAtJobSite ? (
                <div style={styles.notAtJobWarning}>
                  <span>⚠️ {t.notAtJobSite}</span>
                  <small>{t.selectJobFirst}</small>
                </div>
              ) : (
                <span>{t.selectJob}</span>
              )}
            </div>
          )}
        </div>
        <div style={styles.jobSelectorArrow}>▼</div>
      </div>

      {/* Job Modal */}
      {showJobSelector && (
        <div style={styles.modalOverlay} onClick={() => setShowJobSelector(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2>{t.nearbyJobs}</h2>

            {/* Loading indicator while searching for location */}
            {locationLoading && (
              <div style={{
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                fontSize: '15px',
                fontWeight: '500',
                textAlign: 'center'
              }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <div>{language === 'es' ? 'Buscando tu ubicación...' : 'Searching for your location...'}</div>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: 'normal' }}>
                  {language === 'es'
                    ? 'Esto puede tomar unos segundos. Asegúrate de estar afuera con vista al cielo.'
                    : 'This may take a few seconds. Make sure you are outside with a clear view of the sky.'}
                </div>
              </div>
            )}

            {/* Location error or unavailable */}
            {!location && !locationLoading && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📍</div>
                <div style={{ color: '#dc2626', fontWeight: '600', marginBottom: '8px' }}>
                  {language === 'es' ? 'No se puede obtener ubicación' : 'Unable to get location'}
                </div>
                <div style={{ color: '#7f1d1d', fontSize: '13px', marginBottom: '12px' }}>
                  {locationError
                    ? locationError
                    : (language === 'es'
                        ? 'Verifica que la ubicación esté habilitada en la configuración de tu dispositivo.'
                        : 'Check that location is enabled in your device settings.')}
                </div>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    console.log('📍 Retrying location...');
                    await forceRefreshLocation();
                  }}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto'
                  }}
                >
                  <RefreshCw size={16} />
                  {language === 'es' ? 'Reintentar' : 'Try Again'}
                </button>
              </div>
            )}

            {!userAtJobSite && location && !locationLoading && (
              <div style={styles.notAtJobBanner}>
                ⚠️ {t.notAtJobSite}
              </div>
            )}
            
            {currentJobTyped && (
              <div style={styles.currentJobDisplay}>
                <div
                  style={{...styles.jobItem, ...styles.selectedJob, cursor: 'pointer'}}
                  onClick={() => {
                    // Re-confirm current job selection and close modal
                    // This triggers a recalculation with current location data
                    handleJobSelect(currentJobTyped as JobWithDistance);
                  }}
                >
                  <div style={styles.jobItemHeader}>
                    <div style={styles.jobItemName}>
                      {currentJobTyped.job_name} {t.selected}
                    </div>
                    {currentJobTyped.distance && (
                      <div style={styles.jobItemDistance}>{currentJobTyped.distance}</div>
                    )}
                  </div>
                  <div style={styles.jobItemDetails}>
                    {currentJobTyped.bradanna_job_number}
                  </div>
                  <div style={styles.jobItemAddress}>
                    📍 {currentJobTyped.address || ''}, {currentJobTyped.city}, {currentJobTyped.state || ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, textAlign: 'center' }}>
                    Tap to confirm selection
                  </div>
                </div>
              </div>
            )}

            {currentJobTyped && <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />}

            <div style={styles.jobsList}>
              {nearbyJobs.length > 0 ? (
                nearbyJobs
                  .filter(job => {
                    // Exclude current job
                    if (currentJobTyped && job.job_id === currentJobTyped.job_id) return false;

                    return true;
                  })
                  .map(job => (
                    <div
                      key={job.job_id}
                      style={{
                        ...styles.jobItem,
                        ...(job.isWithinGeofence ? styles.withinGeofence : {})
                      }}
                      onClick={() => handleJobSelect(job)}
                    >
                      <div style={styles.jobItemHeader}>
                        <div style={styles.jobItemName}>
                          {job.job_name}
                          {job.isWithinGeofence && (
                            <span style={styles.atLocationBadgeSmall}>📍 {t.atLocation}</span>
                          )}
                        </div>
                        {job.distance && (
                          <div style={styles.jobItemDistance}>{job.distance}</div>
                        )}
                      </div>
                      <div style={styles.jobItemDetails}>
                        {job.bradanna_job_number}
                      </div>
                      <div style={styles.jobItemAddress}>
                        📍 {job.address || ''}, {job.city}, {job.state || ''}
                      </div>
                    </div>
                  ))
              ) : (
                <div style={styles.noNearbyJobs}>
                  {t.noNearbyJobs}
                </div>
              )}
            </div>
            
            <button style={styles.modalClose} onClick={() => setShowJobSelector(false)}>
              {t.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Clock Buttons */}
      <div style={styles.buttonContainer}>
        {renderClockButton()}
      </div>

      {/* Weekly Hours */}
      <div style={styles.weeklyHours}>
        <h2 style={styles.sectionTitle}>{t.weeklyHours}</h2>
        <div style={styles.weekInfo}>{t.weekPeriod}</div>
        
        <div style={styles.weekGrid}>
          {weeklyHours.map((day, index) => {
            const isToday = formatDateForComparison(new Date()) === day.date;
            const hasHours = day.hours > 0;
            return (
              <div
                key={`${day.date}-${index}`}
                style={{
                  ...styles.dayCard,
                  backgroundColor: isToday ? '#e0f2fe' : '#f9fafb',
                  border: isToday ? '2px solid #0284c7' : 'none',
                  cursor: hasHours ? 'pointer' : 'default',
                  opacity: hasHours ? 1 : 0.7
                }}
                onClick={() => hasHours && handleDayClick(day)}
              >
                <div style={styles.dayName}>{day.dayShort}</div>
                <div style={styles.dayDate}>{day.displayDate}</div>
                <div style={{
                  ...styles.dayHours,
                  color: day.hours > 0 ? '#059669' : '#9ca3af'
                }}>
                  {day.hours.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Vacation Hours Banner - Show in weekly summary if vacation scheduled */}
        {vacationSchedule && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 16px',
            marginTop: '8px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 500,
            fontSize: '14px'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              🌴 {language === 'es' ? 'Vacaciones Solicitadas' : 'Vacation Requested'}
            </span>
            <span style={{ fontWeight: 700 }}>
              {vacationSchedule.hours_requested} {language === 'es' ? 'horas' : 'hours'}
            </span>
          </div>
        )}

        <div style={styles.totalRow}>
          <span>{t.totalHours}</span>
          <span style={styles.totalNumber}>
            {totalWeekHours.toFixed(2)} {t.hours}
          </span>
        </div>
      </div>

      {/* Labor Code Modal */}
      {showLaborCodeSelector && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <h3 style={styles.modalTitle}>{t.selectLaborCode}</h3>

            {/* Loading overlay when getting location for clock-in */}
            {(loading || locationLoading) && (
              <div style={{
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                textAlign: 'center'
              }}>
                <Loader2 size={36} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ fontWeight: '600' }}>
                  {language === 'es' ? 'Verificando ubicación...' : 'Verifying location...'}
                </div>
                <div style={{ fontSize: '13px', color: '#3b82f6' }}>
                  {language === 'es'
                    ? 'Por favor espera mientras confirmamos tu ubicación en el sitio.'
                    : 'Please wait while we confirm your location at the job site.'}
                </div>
              </div>
            )}

            {/* Error message if location failed */}
            {error && !loading && !locationLoading && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  {language === 'es' ? 'Error de ubicación' : 'Location Error'}
                </div>
                {error}
              </div>
            )}

            <div style={styles.laborCodeGrid}>
              {getLaborCodes().map(code => (
                <button
                  key={code.code}
                  style={{
                    ...styles.laborCodeButton,
                    opacity: (loading || locationLoading) ? 0.5 : 1,
                    cursor: (loading || locationLoading) ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => handleClockIn(code.code)}
                  disabled={loading || locationLoading}
                >
                  <div style={styles.laborCode}>{code.code}</div>
                  <div style={styles.laborName}>{code.name}</div>
                </button>
              ))}
            </div>
            <button
              style={{
                ...styles.cancelButton,
                opacity: (loading || locationLoading) ? 0.5 : 1
              }}
              onClick={() => {
                setShowLaborCodeSelector(false);
                setError(null);
              }}
              disabled={loading || locationLoading}
            >
              {t.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Lunch Modal - Only shows for special crew with 6+ hour shifts */}
      {showLunchConfirm && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <h3 style={styles.modalTitle}>{t.lunchQuestion}</h3>
            <p style={styles.modalDescription}>{t.lunchNote}</p>
            <div style={styles.lunchButtons}>
              <button
                style={{...styles.lunchButton, ...styles.lunchYesButton}}
                onClick={() => handleLunchConfirm(true)}
                disabled={loading}
              >
                <Check size={20} />
                {t.yesLunch}
              </button>
              <button
                style={{...styles.lunchButton, ...styles.lunchNoButton}}
                onClick={() => handleLunchConfirm(false)}
                disabled={loading}
              >
                <X size={20} />
                {t.noLunch}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Work Confirmation Modal */}
      {showShopWorkConfirm && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <h3 style={styles.modalTitle}>{t.shopWorkConfirmTitle}</h3>
            <p style={styles.modalDescription}>{t.shopWorkConfirmMessage}</p>
            <div style={styles.lunchButtons}>
              <button
                style={{...styles.lunchButton, ...styles.lunchYesButton}}
                onClick={() => handleShopWorkConfirm(true)}
                disabled={loading}
              >
                <Check size={20} />
                {t.shopWorkConfirmYes}
              </button>
              <button
                style={{...styles.lunchButton, ...styles.lunchNoButton}}
                onClick={() => handleShopWorkConfirm(false)}
                disabled={loading}
              >
                <X size={20} />
                {t.shopWorkConfirmNo}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Prompt Modal */}
      {/* Multiple-site picker: shown when standing inside 2+ overlapping
          geofences so the worker explicitly chooses, instead of the app
          silently clocking them into the nearest (wrong) job. */}
      {showMultiSitePrompt && multiSiteOptions.length >= 2 && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <h3 style={styles.modalTitle}>
              {language === 'es' ? '¿En qué trabajo estás?' : 'Which job are you at?'}
            </h3>
            <p style={styles.modalDescription}>
              {language === 'es'
                ? `Estás dentro de ${multiSiteOptions.length} sitios. Selecciona el correcto.`
                : `You're inside ${multiSiteOptions.length} job sites. Tap the correct one.`}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
              {multiSiteOptions.map(job => (
                <button
                  key={job.job_id}
                  onClick={() => handleMultiSitePick(job)}
                  style={{
                    width: '100%', padding: '16px', borderRadius: '10px', border: '2px solid #10b981',
                    backgroundColor: '#ecfdf5', color: '#065f46', fontSize: '17px', fontWeight: 700,
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  📍 {job.job_name}
                  {job.bradanna_job_number && (
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#047857', marginTop: '2px' }}>
                      {job.bradanna_job_number}{job.distance ? ` · ${job.distance}` : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => { multiSiteDismissedRef.current = true; setShowMultiSitePrompt(false); }}
              style={{ width: '100%', padding: '12px', marginTop: '14px', borderRadius: '10px', border: 'none', backgroundColor: '#6b7280', color: 'white', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {showConvertPrompt && targetJob && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <h3 style={styles.modalTitle}>{t.convertPromptTitle}</h3>
            <p style={styles.modalDescription}>
              {t.convertPromptMessage}
              <br /><br />
              <strong>{targetJob.job_name}</strong>
            </p>
            <div style={styles.convertButtons}>
              <button
                style={{...styles.convertButtonOption, backgroundColor: '#10b981'}}
                onClick={() => handleConvertToJob(targetJob)}
                disabled={loading}
              >
                {t.yes}
              </button>
              <button
                style={{...styles.convertButtonOption, backgroundColor: '#6b7280'}}
                onClick={() => {
                  setShowConvertPrompt(false);
                  setTargetJob(null);
                }}
                disabled={loading}
              >
                {t.no}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clock-In Confirmation Modal */}
      {showClockInConfirmation && clockInConfirmDetails && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Check size={64} color="#10b981" />
            </div>
            <h3 style={{ fontSize: '28px', fontWeight: 'bold', margin: '20px 0', textAlign: 'center', color: '#1f2937' }}>
              {language === 'es' ? '¡Entrada Registrada!' : 'Clocked In Successfully!'}
            </h3>
            <div style={{ fontSize: '20px', color: '#374151', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' }}>
              <p style={{ marginBottom: '12px' }}>
                <strong>{clockInConfirmDetails.jobName}</strong>
              </p>
              <p style={{ color: '#6b7280' }}>
                {language === 'es' ? 'Hora: ' : 'Time: '}
                {clockInConfirmDetails.time}
              </p>
            </div>
            <button
              style={{
                width: '100%',
                padding: '18px',
                fontSize: '22px',
                fontWeight: 'bold',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                opacity: checkingSpecialRequirements ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onClick={handleClockInContinue}
              disabled={checkingSpecialRequirements}
            >
              {checkingSpecialRequirements ? (
                <>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                  {language === 'es' ? 'Verificando...' : 'Checking...'}
                </>
              ) : (
                language === 'es' ? 'Continuar al Panel' : 'Continue to Dashboard'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Background Location Disclosure Modal (Google Play requirement) */}
      <BackgroundLocationDisclosure
        isOpen={showBackgroundLocationDisclosure}
        onAllow={handleBackgroundLocationAllow}
        onDecline={handleBackgroundLocationDecline}
        language={language}
      />

      {/* Location Permission Required Modal (blocks clock-in if not "Always") */}
      {showPermissionRequired && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1e293b',
            borderRadius: '16px',
            maxWidth: '400px',
            width: '100%',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              backgroundColor: '#f59e0b',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <MapPin size={32} color="#1e293b" />
              </div>
              <h2 style={{
                color: '#1e293b',
                fontSize: '20px',
                fontWeight: 700,
                margin: '0 0 8px'
              }}>
                {language === 'es' ? 'Permiso de Ubicación Requerido' : 'Location Permission Required'}
              </h2>
              <p style={{
                color: '#1e293b',
                fontSize: '14px',
                margin: 0,
                opacity: 0.8
              }}>
                {language === 'es' ? '"Permitir Siempre" es necesario para fichar' : '"Always Allow" is required to clock in'}
              </p>
            </div>

            {/* Content */}
            <div style={{ padding: '20px' }}>
              <p style={{
                color: '#e2e8f0',
                fontSize: '14px',
                lineHeight: 1.6,
                marginBottom: '16px'
              }}>
                {language === 'es'
                  ? 'Tu tiempo se rastrea por ubicación del sitio de trabajo. Sin ubicación en segundo plano, no podemos rastrear con precisión en qué trabajo estás.'
                  : 'Your time is tracked by job site location. Without background location, we cannot accurately track which job you are working at.'}
              </p>

              {/* Instructions */}
              <div style={{
                backgroundColor: '#0f172a',
                padding: '16px',
                borderRadius: '12px',
                borderLeft: '4px solid #3b82f6',
                marginBottom: '20px'
              }}>
                <p style={{
                  color: '#e2e8f0',
                  fontSize: '14px',
                  margin: '0 0 8px'
                }}>
                  {language === 'es'
                    ? 'Por favor cambia el permiso de ubicación a "Siempre":'
                    : 'Please change location permission to "Always":'}
                </p>
                <p style={{
                  color: '#3b82f6',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  margin: 0
                }}>
                  {Capacitor.getPlatform() === 'ios'
                    ? (language === 'es' ? 'Configuración > Bradanna Field > Ubicación > Siempre' : 'Settings > Bradanna Field > Location > Always')
                    : (language === 'es' ? 'Configuración > Apps > Bradanna Field > Permisos > Ubicación > Permitir siempre' : 'Settings > Apps > Bradanna Field > Permissions > Location > Allow all the time')}
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div style={{
              padding: '0 20px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <button
                onClick={openLocationSettings}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {language === 'es' ? 'Abrir Configuración' : 'Open Settings'}
              </button>
              <button
                onClick={() => setShowPermissionRequired(false)}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: 'transparent',
                  color: '#94a3b8',
                  border: '1px solid #334155',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Entry Modal - Updated to handle ANY old open entry that blocks clock-in */}
      {showOpenEntryModal && openEntry && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <AlertCircle size={64} color="#f59e0b" />
            <h3 style={{ fontSize: '28px', fontWeight: 'bold', margin: '20px 0', textAlign: 'center', color: '#1f2937' }}>
              {language === 'es' ? '¿Olvidó Marcar Salida?' : 'Did You Forget to Clock Out?'}
            </h3>
            <p style={{ fontSize: '20px', color: '#374151', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' }}>
              {language === 'es'
                ? `${new Date(openEntry.clock_in).toLocaleDateString('es', { weekday: 'long', month: 'long', day: 'numeric' })}`
                : `${new Date(openEntry.clock_in).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`
              }
            </p>
            <p style={{ fontSize: '16px', color: '#6b7280', textAlign: 'center', marginBottom: '32px' }}>
              {language === 'es'
                ? `Registró entrada a las ${new Date(openEntry.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                : `You clocked in at ${new Date(openEntry.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
              }
            </p>
            
            <div style={{marginBottom: '32px'}}>
              {/* Location Data Option - Recommended when available */}
              {suggestedExitTime && (
                <div style={{marginBottom: '20px'}}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    padding: '16px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '8px',
                    border: '2px solid #10b981'
                  }}>
                    <input
                      type="radio"
                      name="yesterdayClockOutMethod"
                      value="location_data"
                      checked={forgotClockOutMethod === 'location_data'}
                      onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>
                      {language === 'es' ? 'Usar datos de ubicación (Recomendado)' : 'Use Location Data (Recommended)'}
                    </span>
                  </label>
                  <div style={{marginLeft: '20px', marginTop: '12px', fontSize: '18px', color: '#047857'}}>
                    <MapPin size={16} style={{display: 'inline', marginRight: '6px', verticalAlign: 'middle'}} />
                    {language === 'es'
                      ? `Salió de ${suggestedExitTime.jobName} a las ${suggestedExitTime.eventTime}`
                      : `Left ${suggestedExitTime.jobName} at ${suggestedExitTime.eventTime}`}
                  </div>
                </div>
              )}

              <div style={{marginBottom: '20px'}}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: crewAverageTime ? 'pointer' : 'not-allowed',
                  opacity: crewAverageTime ? 1 : 0.5,
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '2px solid ' + (forgotClockOutMethod === 'crew_average' ? '#10b981' : '#e5e7eb')
                }}>
                  <input
                    type="radio"
                    name="yesterdayClockOutMethod"
                    value="crew_average"
                    checked={forgotClockOutMethod === 'crew_average'}
                    onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                    disabled={!crewAverageTime}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '18px', fontWeight: '500' }}>
                    {language === 'es' ? 'Usar hora del equipo' : 'Use crew time'}
                  </span>
                </label>
                {forgotClockOutMethod === 'crew_average' && crewAverageTime && (
                  <div style={{marginLeft: '20px', marginTop: '12px', fontSize: '24px', color: '#10b981', fontWeight: 'bold'}}>
                    {crewAverageTime}
                  </div>
                )}
                {!crewAverageTime && (
                  <div style={{marginLeft: '20px', marginTop: '8px', fontSize: '14px', color: '#ef4444'}}>
                    {language === 'es' ? 'No disponible' : 'Not available'}
                  </div>
                )}
              </div>

              <div style={{marginBottom: '20px'}}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '2px solid ' + (forgotClockOutMethod === 'manual' ? '#10b981' : '#e5e7eb')
                }}>
                  <input
                    type="radio"
                    name="yesterdayClockOutMethod"
                    value="manual"
                    checked={forgotClockOutMethod === 'manual'}
                    onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontSize: '18px', fontWeight: '500' }}>
                    {language === 'es' ? 'Ingresar hora manualmente' : 'Enter time manually'}
                  </span>
                </label>
                {forgotClockOutMethod === 'manual' && (
                  <div style={{marginLeft: '20px', marginTop: '16px'}}>
                    <CustomTimePicker
                      value={manualClockOutTime}
                      onChange={(val) => setManualClockOutTime(val)}
                      label={language === 'es' ? 'Seleccionar Hora' : 'Select Time'}
                      style={{
                        border: '2px solid #10b981',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              <button
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: loading ||
                    (forgotClockOutMethod === 'crew_average' && !crewAverageTime) ||
                    (forgotClockOutMethod === 'location_data' && !suggestedExitTime)
                    ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  cursor: loading ||
                    (forgotClockOutMethod === 'crew_average' && !crewAverageTime) ||
                    (forgotClockOutMethod === 'location_data' && !suggestedExitTime)
                    ? 'not-allowed' : 'pointer'
                }}
                onClick={handleCloseYesterdayEntry}
                disabled={loading ||
                  (forgotClockOutMethod === 'crew_average' && !crewAverageTime) ||
                  (forgotClockOutMethod === 'location_data' && !suggestedExitTime)}
              >
                {language === 'es' ? 'Confirmar Salida' : 'Confirm Clock Out'}
              </button>
              <button
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: 'white',
                  color: '#6b7280',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onClick={() => {
                  setShowOpenEntryModal(false);
                  setOpenEntry(null);
                }}
                disabled={loading}
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Forgot Clock Out Modal */}
      {showForgotClockOut && (
        <div style={styles.modal}>
          <div style={styles.modalContentSmall}>
            <AlertCircle size={48} color="#f59e0b" />
            <h3 style={styles.modalTitle}>
              {language === 'es' ? '¿Olvidó Marcar Salida?' : 'Forgot to Clock Out?'}
            </h3>
            
            <div style={{marginBottom: '20px'}}>
              {/* Location Data Option - Recommended when available */}
              {suggestedExitTime && (
                <div style={{marginBottom: '12px', padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '2px solid #10b981'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="radio"
                      name="clockOutMethod"
                      value="location_data"
                      checked={forgotClockOutMethod === 'location_data'}
                      onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                    />
                    <span style={{fontWeight: 'bold', color: '#059669'}}>
                      {language === 'es' ? 'Usar datos de ubicación (Recomendado)' : 'Use Location Data (Recommended)'}
                    </span>
                  </label>
                  <div style={{marginLeft: '26px', fontSize: '14px', color: '#047857', marginTop: '4px'}}>
                    <MapPin size={14} style={{display: 'inline', marginRight: '4px', verticalAlign: 'middle'}} />
                    {language === 'es'
                      ? `Salió de ${suggestedExitTime.jobName} a las ${suggestedExitTime.eventTime}`
                      : `Left ${suggestedExitTime.jobName} at ${suggestedExitTime.eventTime}`}
                  </div>
                </div>
              )}

              <div style={{marginBottom: '12px'}}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: crewAverageTime ? 'pointer' : 'not-allowed',
                  opacity: crewAverageTime ? 1 : 0.5
                }}>
                  <input
                    type="radio"
                    name="clockOutMethod"
                    value="crew_average"
                    checked={forgotClockOutMethod === 'crew_average'}
                    onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                    disabled={!crewAverageTime}
                  />
                  <span>{t.useCrewAverage}</span>
                </label>
                {forgotClockOutMethod === 'crew_average' && (
                  <div style={{marginLeft: '26px', fontSize: '14px', color: '#6b7280'}}>
                    {crewAverageTime ? crewAverageTime :
                      (language === 'es' ? 'No hay datos del equipo disponibles' : 'No crew data available')
                    }
                  </div>
                )}
                {!crewAverageTime && (
                  <div style={{marginLeft: '26px', fontSize: '12px', color: '#ef4444'}}>
                    {language === 'es' ? 'Sin promedio del equipo - debe ingresar hora manual' : 'No crew average - must enter time manually'}
                  </div>
                )}
              </div>

              <div style={{marginBottom: '12px'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                  <input
                    type="radio"
                    name="clockOutMethod"
                    value="manual"
                    checked={forgotClockOutMethod === 'manual'}
                    onChange={(e) => setForgotClockOutMethod(e.target.value as 'location_data' | 'crew_average' | 'manual')}
                  />
                  <span>{t.enterTimeManually}</span>
                </label>
                {forgotClockOutMethod === 'manual' && (
                  <div style={{marginLeft: '26px', marginTop: '8px'}}>
                    <CustomTimePicker
                      value={manualClockOutTime}
                      onChange={(val) => setManualClockOutTime(val)}
                      label={language === 'es' ? 'Seleccionar Hora' : 'Select Time'}
                    />
                  </div>
                )}
              </div>

              {forgotClockOutMethod === 'manual' && (
                <div style={{fontSize: '12px', color: '#f59e0b', marginTop: '8px'}}>
                  {t.manualEntryNote}
                </div>
              )}
            </div>

            <div style={{display: 'flex', gap: '12px'}}>
              <button
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
                onClick={handleForgotClockOut}
                disabled={loading ||
                  (forgotClockOutMethod === 'crew_average' && !crewAverageTime) ||
                  (forgotClockOutMethod === 'location_data' && !suggestedExitTime)}
              >
                {t.confirm}
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowForgotClockOut(false)}
                disabled={loading}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location retry modal — GPS failed during clock in/out. Friendlier than
          the generic error modal: one big button retries GPS and re-fires the
          interrupted clock action. */}
      {locationRetry && (
        <div style={styles.errorModal}>
          <div style={styles.errorContent}>
            <MapPin size={48} color="#3b82f6" />
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
              {language === 'es' ? 'No encontramos tu ubicación' : "Can't find your location"}
            </div>
            <div style={{ fontSize: '15px', color: locationRetryStatus === 'failed' ? '#b45309' : '#4b5563', lineHeight: 1.4 }}>
              {locationRetryStatus === 'failed'
                ? (language === 'es'
                    ? 'Aún no encontramos tu ubicación. Cierra la aplicación por completo, ábrela de nuevo e intenta otra vez.'
                    : 'Still not finding you. Fully close the app, reopen it, then try again.')
                : (language === 'es'
                    ? 'Toca el botón y lo intentamos de nuevo.'
                    : "Tap the button and we'll try again.")}
            </div>
            <button
              onClick={retryLocationAndRefire}
              disabled={locationRetryStatus === 'trying'}
              style={{
                padding: '14px 32px',
                backgroundColor: locationRetryStatus === 'trying' ? '#93c5fd' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '17px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                minWidth: '230px'
              }}
            >
              {locationRetryStatus === 'trying' ? (
                <>
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  {language === 'es' ? 'Buscando…' : 'Searching…'}
                </>
              ) : (
                <>
                  <RefreshCw size={20} />
                  {language === 'es' ? 'Buscar mi ubicación' : 'Get My Location'}
                </>
              )}
            </button>
            <button
              onClick={() => { setLocationRetry(null); setLocationRetryStatus('idle'); }}
              style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '15px', fontWeight: 600, cursor: 'pointer', padding: '8px' }}
            >
              {language === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error && (
        <div style={styles.errorModal}>
          <div style={styles.errorContent}>
            <AlertCircle size={48} color="#ef4444" />
            <div style={styles.errorText}>{error}</div>
            <button
              style={styles.errorButton}
              onClick={() => setError(null)}
            >
              {t.ok}
            </button>
          </div>
        </div>
      )}

      {/* Day Details Modal */}
      {showDayDetails && selectedDay && (
        <div style={styles.modal}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{marginBottom: '16px', fontSize: '20px', fontWeight: 'bold'}}>
              {selectedDay.dayName} - {selectedDay.displayDate}
            </h2>
            <p style={{marginBottom: '16px', color: '#6b7280'}}>
              {language === 'es' ? 'Total de horas' : 'Total hours'}: <strong style={{color: '#059669'}}>{selectedDay.hours.toFixed(2)}</strong>
            </p>

            <div style={{maxHeight: '400px', overflowY: 'auto'}}>
              {dayEntries.map((entry, index) => {
                const clockIn = new Date(entry.clock_in);
                const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;

                return (
                  <div
                    key={entry.entry_id}
                    style={{
                      backgroundColor: '#f9fafb',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{fontWeight: 'bold', marginBottom: '8px', color: '#111827'}}>
                      {entry.job_name}
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', color: '#6b7280'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between'}}>
                        <span>{language === 'es' ? 'Entrada' : 'Clock In'}:</span>
                        <span style={{fontWeight: '500', color: '#111827'}}>
                          {clockIn.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                        </span>
                      </div>
                      <div style={{display: 'flex', justifyContent: 'space-between'}}>
                        <span>{language === 'es' ? 'Salida' : 'Clock Out'}:</span>
                        <span style={{fontWeight: '500', color: '#111827'}}>
                          {clockOut
                            ? clockOut.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                            : (language === 'es' ? 'Aún trabajando' : 'Still working')
                          }
                        </span>
                      </div>
                      {entry.hours_worked !== null && entry.hours_worked !== undefined && (
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                          <span>{language === 'es' ? 'Horas trabajadas' : 'Hours Worked'}:</span>
                          <span style={{fontWeight: '600', color: '#059669'}}>
                            {parseFloat(entry.hours_worked.toString()).toFixed(2)} {language === 'es' ? 'hrs' : 'hrs'}
                          </span>
                        </div>
                      )}
                      {entry.lunch_taken && (
                        <div style={{fontSize: '12px', color: '#f59e0b', marginTop: '4px'}}>
                          🍽️ {language === 'es' ? 'Almuerzo deducido' : 'Lunch deducted'} ({entry.lunch_minutes || 30} {language === 'es' ? 'min' : 'min'})
                        </div>
                      )}
                      {entry.labor_code && (
                        <div style={{fontSize: '12px', color: '#3b82f6', marginTop: '4px'}}>
                          {language === 'es' ? 'Código' : 'Code'}: {entry.labor_code}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              style={styles.modalClose}
              onClick={() => {
                setShowDayDetails(false);
                setSelectedDay(null);
                setDayEntries([]);
              }}
            >
              {t.cancel}
            </button>
          </div>
        </div>
      )}

      {/* Timecard Review Modal (Friday approval) */}
      {showTimecardReview && timecardReviewData && (
        <TimecardReviewModal
          isOpen={showTimecardReview}
          onClose={handleTimecardClose}
          onApprove={handleTimecardApprove}
          onSkip={handleTimecardSkip}
          weekStartDate={timecardReviewData.weekStartDate}
          weekEndDate={timecardReviewData.weekEndDate}
          entries={timecardReviewData.entries}
          crewAverageClockOut={timecardReviewData.crewAverageClockOut}
          totalHours={timecardReviewData.totalHours}
          canSkip={timecardReviewData.canSkip}
          language={language}
          apiCall={apiCall}
          jobs={jobs.map(j => ({ job_id: j.job_id, job_name: j.job_name, bradanna_job_number: j.bradanna_job_number }))}
        />
      )}

      {/* Unknown Job Photo Capture Modal */}
      {showUnknownJobCamera && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.9)',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1a1a2e',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <CameraIcon size={48} color="#f97316" style={{ margin: '0 auto 12px' }} />
              <h3 style={{ color: 'white', fontSize: '20px', marginBottom: '8px' }}>
                {language === 'es' ? 'Ubicación No Detectada' : 'Location Not Detected'}
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.5' }}>
                {language === 'es'
                  ? 'No detectamos que estés en un sitio de trabajo. Por favor toma una foto del sitio para verificar tu ubicación.'
                  : 'We do not detect you are at a job site. Please take a photo of the site to verify your location.'}
              </p>
            </div>

            {uploadingPhoto ? (
              <div style={{ padding: '20px' }}>
                <Loader2 size={48} color="#22c55e" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ color: 'white', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                  {language === 'es' ? 'Registrando entrada...' : 'Clocking in...'}
                </p>
                <p style={{ color: '#94a3b8', fontSize: '14px' }}>
                  {language === 'es' ? 'Por favor espere, no cierre esta pantalla' : 'Please wait, do not close this screen'}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={handleTakeUnknownJobPhoto}
                  style={{
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <CameraIcon size={20} />
                  {language === 'es' ? 'Tomar Foto' : 'Take Photo'}
                </button>

                <button
                  onClick={handleCancelUnknownJobClockIn}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#94a3b8',
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default ClockInOut;